import { useParams, Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Eye, Save, Monitor, Tablet, Smartphone, Plus, GripVertical, Trash2, Copy as CopyIcon,
  EyeOff, ChevronUp, ChevronDown, Settings2, ExternalLink, Palette, Type, Image as ImageIcon,
  Calendar, Users, Crown, Quote, MapPin, MessageSquare, Mail, Sparkles, Layers, PanelTopOpen,
  PanelTopClose, SquareDashedBottom, Link2, PanelLeftClose, PanelLeftOpen,
  LayoutTemplate, X, BarChart3, Ticket, ArrowLeft, Play, Brush, Heart, LayoutGrid, List,
  Undo2, Redo2,
} from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { usePersistedState } from "@/lib/persisted-state";
import { useEventMeta } from "@/lib/event-meta";
import { loadTicketEngine, syncTicketEngine } from "@/lib/ticket-engine";
import { buildBlocksFromTemplate, getTemplateMeta, PAGE_BUILDER_TEMPLATES } from "@/lib/page-builder-templates";
import { API_BASE } from "@/lib/utils";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { listMedia, resolveMediaUrl, uploadMediaFile } from "@/lib/media-api";
import { patchEventCover } from "@/lib/event-cover";
import { useAdminEvents } from "@/contexts/AdminEventsContext";
import MediaPickerModal from "@/components/MediaPickerModal";
import SpeakersGrid from "@/components/SpeakersGrid";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

const PREFILLED_BLOCKS = [
  { type: "hero", label: "Hero", icon: Sparkles, body: "Event title + CTA", source: "Manual fields + event cover" },
  { type: "metrics", label: "Metrics", icon: Layers, body: "Dates/venue/timezone", source: "Event data" },
  { type: "schedule", label: "Schedule", icon: Calendar, body: "Sessions list", source: "Sessions dataset" },
  { type: "speakers", label: "Speakers", icon: Users, body: "Speaker cards", source: "Speakers API" },
  { type: "tickets", label: "Tickets", icon: Crown, body: "Tier cards", source: "Ticket engine" },
  { type: "sponsors", label: "Sponsors", icon: Crown, body: "Logo strip", source: "Sponsor list" },
  { type: "exhibitors", label: "Exhibitors", icon: LayoutGrid, body: "Booth directory", source: "Exhibitor list" },
  { type: "faq", label: "FAQ", icon: MessageSquare, body: "Question/answer list", source: "Block props" },
  { type: "map", label: "Venue map", icon: MapPin, body: "Venue details", source: "Event data + block props" },
  { type: "newsletter", label: "Newsletter", icon: Mail, body: "Email CTA", source: "Block props" },
  { type: "cta", label: "Closing CTA", icon: Sparkles, body: "Final call to action", source: "Block props" },
];

const GENERIC_BLOCKS = [
  { type: "headline", label: "Headline", icon: Type, body: "Prominent heading section", source: "Block props" },
  { type: "list", label: "List", icon: Layers, body: "Bullet list or key points", source: "Block props" },
  { type: "gallery", label: "Gallery", icon: ImageIcon, body: "Image grid", source: "Media library" },
  { type: "image", label: "Single image", icon: ImageIcon, body: "Single image with caption", source: "Media library" },
  { type: "carousel", label: "Image carousel", icon: ImageIcon, body: "Swipe/slide image set", source: "Media library" },
  { type: "quote", label: "Quotes", icon: Quote, body: "Testimonial or quote block", source: "Block props" },
  { type: "text", label: "Text", icon: Type, body: "Rich heading/body section", source: "Block props" },
  { type: "video", label: "Video (YouTube)", icon: LayoutTemplate, body: "Embedded YouTube section", source: "Video URL" },
  { type: "contact", label: "Contact", icon: Mail, body: "Contact details + form CTA", source: "Block props" },
  { type: "socials", label: "Socials", icon: Link2, body: "Social profile links", source: "Block props" },
  { type: "sponsors", label: "Sponsors", icon: Crown, body: "Sponsor grid (multi-row)", source: "Sponsors/API" },
  { type: "charts", label: "Charts", icon: BarChart3, body: "Simple data highlight chart cards", source: "Block props" },
  { type: "faq", label: "FAQs", icon: MessageSquare, body: "Question/answer list", source: "Block props" },
  { type: "menu", label: "Menu", icon: PanelTopOpen, body: "Program/menu list", source: "Block props" },
  { type: "people", label: "People", icon: Users, body: "Team/speakers list", source: "Block props" },
  { type: "pricing", label: "Pricing", icon: Ticket, body: "Pricing/package cards", source: "Block props" },
  { type: "logoStrip", label: "Logo strip", icon: Layers, body: "Partners/sponsors logos", source: "Block props / media" },
  { type: "accordion", label: "Accordion", icon: PanelTopClose, body: "Collapsible content", source: "Block props" },
  { type: "divider", label: "Divider", icon: SquareDashedBottom, body: "Visual separator", source: "Block props" },
  { type: "button", label: "Button", icon: Link2, body: "Standalone button", source: "Block props" },
];

const GOOGLE_FONTS = [
  { label: "DM Sans", value: "\"DM Sans\", system-ui, sans-serif", family: "DM Sans:wght@400;500;700" },
  { label: "Inter", value: "\"Inter\", system-ui, sans-serif", family: "Inter:wght@400;500;700" },
  { label: "Poppins", value: "\"Poppins\", system-ui, sans-serif", family: "Poppins:wght@400;500;700" },
  { label: "Merriweather", value: "\"Merriweather\", serif", family: "Merriweather:wght@400;700" },
  { label: "Playfair Display", value: "\"Playfair Display\", serif", family: "Playfair+Display:wght@400;600;700" },
  { label: "Roboto Mono", value: "\"Roboto Mono\", ui-monospace, monospace", family: "Roboto+Mono:wght@400;600" },
];

const COLOR_BUCKET = ["var(--primary)", "#E25D3A", "#60A5FA", "#A78BFA", "#FAFAFA", "#09090B", "#27272A", "#71717A", "#16A34A", "#F59E0B"];

const BLOCK_INDEX = [...PREFILLED_BLOCKS, ...GENERIC_BLOCKS];

const SECTION_CATEGORY_GROUPS = [
  {
    label: "Introduce",
    categories: [
      {
        id: "intro",
        label: "Intro",
        types: [
          { type: "hero", thumb: "hero-full" },
          { type: "headline", thumb: "headline-split" },
          { type: "cta", thumb: "cta-dark" },
          { type: "text", thumb: "text-accent" },
        ],
      },
      {
        id: "about",
        label: "About",
        types: [
          { type: "text", thumb: "about-text" },
          { type: "headline", thumb: "headline-centered" },
          { type: "image", thumb: "image-inline" },
        ],
      },
      {
        id: "contact",
        label: "Contact",
        types: [
          { type: "contact", thumb: "contact" },
          { type: "map", thumb: "map" },
          { type: "newsletter", thumb: "newsletter" },
        ],
      },
      {
        id: "team",
        label: "Team",
        types: [
          { type: "speakers", thumb: "speakers" },
          { type: "people", thumb: "people-grid" },
        ],
      },
      {
        id: "faqs",
        label: "FAQs",
        types: [
          { type: "faq", thumb: "faq" },
          { type: "accordion", thumb: "accordion" },
        ],
      },
    ],
  },
  {
    label: "Event",
    categories: [
      {
        id: "program",
        label: "Program",
        types: [
          { type: "schedule", thumb: "schedule" },
          { type: "menu", thumb: "menu" },
          { type: "metrics", thumb: "metrics" },
        ],
      },
      {
        id: "tickets",
        label: "Tickets",
        types: [
          { type: "tickets", thumb: "tickets" },
          { type: "pricing", thumb: "pricing" },
        ],
      },
      {
        id: "sponsors",
        label: "Sponsors",
        types: [
          { type: "sponsors", thumb: "sponsors" },
          { type: "logoStrip", thumb: "logo-strip" },
        ],
      },
    ],
  },
  {
    label: "Content",
    categories: [
      {
        id: "media",
        label: "Media",
        types: [
          { type: "gallery", thumb: "gallery" },
          { type: "carousel", thumb: "carousel" },
          { type: "video", thumb: "video" },
          { type: "quote", thumb: "quote" },
        ],
      },
      {
        id: "social",
        label: "Social",
        types: [
          { type: "socials", thumb: "socials" },
          { type: "list", thumb: "list-stacked" },
          { type: "charts", thumb: "charts" },
        ],
      },
    ],
  },
  {
    label: "Utility",
    categories: [
      {
        id: "more",
        label: "More",
        types: [
          { type: "divider", thumb: "divider" },
          { type: "button", thumb: "button" },
        ],
      },
    ],
  },
];

function sectionTypesForCategory(categoryId) {
  for (const group of SECTION_CATEGORY_GROUPS) {
    const cat = group.categories.find((c) => c.id === categoryId);
    if (cat) return cat.types;
  }
  return SECTION_CATEGORY_GROUPS[0].categories[0].types;
}

function blockMetaForType(type) {
  return BLOCK_INDEX.find((b) => b.type === type) || { type, label: type, icon: Type, body: "" };
}

const BLOCK_PREVIEW_TEXT = {
  hero: "Hero + CTA",
  metrics: "Key event stats",
  schedule: "Session timeline",
  speakers: "Speaker cards",
  tickets: "Ticket tiers",
  sponsors: "Sponsor logos",
  faq: "Q&A rows",
  map: "Venue map",
  newsletter: "Email capture",
  cta: "Action banner",
  headline: "Big heading",
  list: "Bullet list",
  gallery: "Image grid",
  image: "Single image",
  carousel: "Sliding images",
  quote: "Highlighted quote",
  text: "Rich text",
  video: "YouTube embed",
  contact: "Contact info",
  socials: "Social links",
  charts: "Metric cards",
  menu: "Menu/program list",
  people: "People grid",
  pricing: "Pricing plans",
  logoStrip: "Partner strip",
  accordion: "Expandable rows",
  divider: "Section divider",
  button: "Standalone button",
};

const SOURCE_HINTS = {
  hero: "Prefilled. Pulls event cover from Event Settings. Edit text here; replace cover in event setup or media URL.",
  metrics: "Prefilled. Pulls dates/venue from event data.",
  schedule: "Prefilled. Pulls sessions from schedule manager.",
  speakers: "Prefilled. Pulls speakers/VVIP from Attendees > Speakers/VIP.",
  tickets: "Prefilled. Pulls ticket tiers and status from Ticket Engine.",
  sponsors: "Prefilled. Pulls sponsor names/logos from sponsor list.",
  faq: "Prefilled block with editable items in right panel.",
  map: "Prefilled. Venue is event data; address/embed link editable here.",
  newsletter: "Prefilled visual block; connect form endpoint in integration settings.",
  cta: "Prefilled CTA text editable here.",
  text: "Generic block; fully manual content.",
  image: "Generic block; choose media from library or upload new asset.",
  gallery: "Generic block; choose up to 6 images from media library.",
  logoStrip: "Generic block; ideal for sponsor logos/partner marks.",
  accordion: "Generic block; add unlimited FAQ/agenda/content rows.",
  quote: "Generic quote/testimonial block.",
  divider: "Generic visual spacer/divider.",
  button: "Generic standalone button.",
};

const initialBlocks = [
  { id: "b1", type: "hero", visible: true, props: { eyebrow: "Apr 14 — 16 · 2026", title: "Stride", titleAccent: "/ 2026", body: "The B2B Operators' Conference.", cta: "Save your seat", bg: "image", imageUrl: "" } },
  { id: "b2", type: "metrics", visible: true, props: {} },
  { id: "b3", type: "sponsors", visible: true, props: { caption: "Backed by operators across the network" } },
  { id: "b4", type: "schedule", visible: true, props: { eyebrow: "Schedule", title: "Day one — building blocks" } },
  { id: "b5", type: "speakers", visible: true, props: { eyebrow: "Speakers", title: "A small, deliberate roster." } },
  { id: "b6", type: "faq", visible: false, props: { items: [{ q: "Can I transfer my ticket?", a: "Yes, from your attendee portal." }, { q: "Are workshops included?", a: "Only if your ticket includes workshop add-ons." }] } },
  { id: "b7", type: "cta", visible: true, props: { title: "Be in the room.", cta: "Register · from $449" } },
];

const themes = {
  "Aivory Default": { accent: "var(--primary)", bg: "#09090B", surface: "#121214" },
  Terracotta: { accent: "#E25D3A", bg: "#0E0A08", surface: "#171210" },
  "Ice Blue": { accent: "#60A5FA", bg: "#08090E", surface: "#11131C" },
  "Editorial Cream": { accent: "#FAFAFA", bg: "#1A1815", surface: "#22201C" },
};

const devices = [
  { id: "desktop", icon: Monitor, w: 1200 },
  { id: "tablet", icon: Tablet, w: 820 },
  { id: "mobile", icon: Smartphone, w: 390 },
];

const GENERIC_BLOCK_TEMPLATES = {
  text: [
    { label: "Intro copy", props: { title: "About this event", body: "Use this section to explain your value proposition clearly in 2-4 lines." } },
    { label: "Feature story", props: { title: "What makes this different", body: "Highlight your format, outcomes, and who should attend." } },
  ],
  headline: [
    { label: "Centered headline", layout: "centered", props: { title: "Start the adventure today.", body: "A crisp statement under your nav.", align: "center" } },
    { label: "Left intro", layout: "left", props: { title: "Why attend", body: "Tell visitors what they will get in one paragraph.", align: "left" } },
  ],
  list: [
    { label: "Stacked list", layout: "stacked", props: { title: "What you'll learn", items: ["Practical frameworks", "Real case studies", "Ready-to-use templates"] } },
    { label: "3-column features", layout: "columns", props: { title: "What you'll learn", items: ["Practical frameworks", "Real case studies", "Ready-to-use templates"] } },
    { label: "Icon row", layout: "icon-row", props: { title: "What you'll learn", items: ["Practical frameworks", "Real case studies", "Ready-to-use templates"] } },
  ],
  gallery: [
    { label: "3-up gallery", layout: "grid-3", props: { images: [] } },
    { label: "6-up showcase", layout: "grid-6", props: { images: [] } },
  ],
  image: [
    { label: "Hero image", props: { src: "", caption: "Main visual" } },
    { label: "Inline image", props: { src: "", caption: "Supporting visual" } },
  ],
  carousel: [
    { label: "Auto carousel", props: { images: [], autoplay: true } },
    { label: "Manual carousel", props: { images: [], autoplay: false } },
  ],
  quote: [
    { label: "Single testimonial", props: { quote: "This was the best event we attended all year.", author: "Attendee" } },
    { label: "Operator quote", props: { quote: "Actionable, practical, and high-signal.", author: "Community lead" } },
  ],
  video: [
    { label: "Talk highlight", props: { title: "Watch the highlight", youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" } },
    { label: "Trailer", props: { title: "Event trailer", youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" } },
  ],
  contact: [
    { label: "Simple contact", props: { title: "Contact us", email: "hello@example.com", phone: "+65 1234 5678", cta: "Send enquiry" } },
    { label: "Sales contact", props: { title: "Speak to our team", email: "sales@example.com", phone: "+65 8888 8888", cta: "Contact sales" } },
  ],
  socials: [
    { label: "Brand socials", props: { title: "Follow us", links: [{ label: "Instagram", url: "#" }, { label: "LinkedIn", url: "#" }] } },
    { label: "Community links", props: { title: "Join our channels", links: [{ label: "Telegram", url: "#" }, { label: "Discord", url: "#" }] } },
  ],
  sponsors: [
    { label: "Logo wall", props: { caption: "Trusted by sponsors" } },
    { label: "Partner strip", props: { caption: "Our partners" } },
  ],
  charts: [
    { label: "3 KPI cards", layout: "columns", props: { title: "Event highlights", stats: [{ label: "Attendees", value: "2,400" }, { label: "Sessions", value: "46" }, { label: "NPS", value: "62" }] } },
    { label: "Growth stats", layout: "stacked", props: { title: "Year-on-year growth", stats: [{ label: "Registrations", value: "+38%" }, { label: "Sponsors", value: "+22%" }, { label: "Revenue", value: "+41%" }] } },
  ],
  faq: [
    { label: "General FAQ", props: { title: "FAQ", items: [{ q: "Can I transfer my ticket?", a: "Yes, from your attendee portal." }] } },
    { label: "Venue FAQ", props: { title: "Venue FAQ", items: [{ q: "Is parking available?", a: "Yes, paid parking is available onsite." }] } },
  ],
  menu: [
    { label: "Program menu", props: { title: "Program", items: [{ name: "Opening", details: "09:00" }, { name: "Lunch", details: "12:30" }] } },
    { label: "Food menu", props: { title: "F&B", items: [{ name: "Breakfast", details: "Buffet" }, { name: "Coffee break", details: "Snacks" }] } },
  ],
  people: [
    { label: "Team grid", layout: "columns", props: { title: "Meet the team", items: [{ name: "Alex", role: "Host" }, { name: "Priya", role: "Producer" }, { name: "Sam", role: "Moderator" }] } },
    { label: "Team list", layout: "stacked", props: { title: "Advisory board", items: [{ name: "Rae", role: "Growth" }, { name: "Noah", role: "AI" }] } },
  ],
  pricing: [
    { label: "3-tier cards", layout: "columns", props: { title: "Pricing", plans: [{ name: "Standard", price: "$199" }, { name: "Pro", price: "$399" }, { name: "VIP", price: "$799" }] } },
    { label: "Compact tiers", layout: "compact", props: { title: "Bundles", plans: [{ name: "Conference", price: "$299" }, { name: "Conference + AI Track", price: "$499" }] } },
  ],
  accordion: [
    { label: "Simple accordion", props: { title: "More details", items: [{ title: "Item 1", body: "Details" }] } },
    { label: "Policy accordion", props: { title: "Policies", items: [{ title: "Cancellation", body: "See ticket terms." }] } },
  ],
  logoStrip: [
    { label: "Sponsor strip", props: { title: "Trusted by", logos: [] } },
    { label: "Customer strip", props: { title: "Used by", logos: [] } },
  ],
  divider: [
    { label: "Subtle divider", props: { style: "line", spacing: 32 } },
    { label: "Wide spacer", props: { style: "line", spacing: 56 } },
  ],
  button: [
    { label: "Primary CTA", props: { label: "Register now", href: "https://" } },
    { label: "Secondary CTA", props: { label: "Learn more", href: "https://" } },
  ],
};

export default function PageBuilder() {
  const { id } = useParams();
  const { refreshEvents } = useAdminEvents();
  const e = useEventMeta(id);
  const resolvedEventId = String(e.id || id || "").trim();
  // Public EventLanding loads published state by canonical evt_* id only.
  const canonicalEventId = resolvedEventId.startsWith("evt_")
    ? resolvedEventId
    : String(id || "").trim().startsWith("evt_")
      ? String(id).trim()
      : resolvedEventId;
  const [speakersPreview, setSpeakersPreview] = useState([]);
  const [sessionsData, setSessionsData] = useState([]);
  const [sponsorsData, setSponsorsData] = useState([]);
  const [ticketRows, setTicketRows] = useState([]);
  const [openGroup, setOpenGroup] = useState("none");
  const mediaInputRef = useRef(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewFont, setPreviewFont] = useState('"DM Sans", system-ui, sans-serif');
  const [previewSizePx, setPreviewSizePx] = useState(16);
  const [sectionsPanelOpen, setSectionsPanelOpen] = useState(false);
  const [stylesPanelOpen, setStylesPanelOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [inspectorTab, setInspectorTab] = useState("content");
  const [genericPicker, setGenericPicker] = useState({ open: false, type: null, index: null, replaceId: null });
  const [sectionPicker, setSectionPicker] = useState({ open: false, index: null, q: "", category: "intro", view: "grid" });
  const [publishedSnap, setPublishedSnap] = useState(null);
  const [exhibitorsData, setExhibitorsData] = useState([]);
  const historyRef = useRef({ past: [], future: [] });
  const [historyTick, setHistoryTick] = useState(0);

  const tenantStorageKey = useMemo(() => {
    try {
      const raw = localStorage.getItem("aivoryems.auth.session");
      const session = raw ? JSON.parse(raw) : null;
      return String(session?.tenantId || session?.orgId || "default");
    } catch {
      return "default";
    }
  }, []);

  const savedSectionsKey = `page-builder-saved-sections.${tenantStorageKey}`;
  const [savedSections, setSavedSections] = useState(() => {
    try {
      const raw = localStorage.getItem(savedSectionsKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(savedSectionsKey, JSON.stringify(savedSections.slice(0, 40)));
    } catch {
      /* ignore */
    }
  }, [savedSections, savedSectionsKey]);

  const publishFingerprint = (state) => JSON.stringify({
    blocks: state?.blocks,
    themeName: state?.themeName,
    templateId: state?.templateId,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/state/${encodeURIComponent(`page-builder-published.${canonicalEventId}`)}`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) return;
        const payload = await res.json();
        if (!cancelled) setPublishedSnap(payload?.value || null);
      } catch {
        /* keep null */
      }
    })();
    return () => { cancelled = true; };
  }, [canonicalEventId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [speakersRes, sessionsRes, bootstrapRes, exhibitorsRes] = await Promise.all([
          fetch(`${API_BASE}/api/events/${id}/speakers`),
          fetch(`${API_BASE}/api/admin/events/${id}/sessions`),
          fetch(`${API_BASE}/api/bootstrap-data`),
          fetch(`${API_BASE}/api/events/${id}/exhibitors`, { headers: getAuthHeaders() }),
        ]);
        if (speakersRes.ok) {
          const data = await speakersRes.json();
          if (!cancelled && Array.isArray(data.speakers)) setSpeakersPreview(data.speakers);
        }
        if (sessionsRes.ok) {
          const d = await sessionsRes.json();
          if (!cancelled) setSessionsData(Array.isArray(d.sessions) ? d.sessions : []);
        }
        if (bootstrapRes.ok) {
          const payload = await bootstrapRes.json();
          const data = payload?.data || {};
          if (!cancelled) {
            setSponsorsData(Array.isArray(data.sponsors) ? data.sponsors : []);
          }
        }
        if (exhibitorsRes.ok) {
          const data = await exhibitorsRes.json();
          if (!cancelled) setExhibitorsData(Array.isArray(data.exhibitors) ? data.exhibitors : []);
        }
      } catch {
        // Keep current state when API is unavailable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await syncTicketEngine(id);
      } catch {
        // Keep local cache only.
      }
      const engine = loadTicketEngine(id);
      const rows = Array.isArray(engine?.tickets)
        ? engine.tickets.filter((t) => t?.type === "TIER")
        : [];
      if (!cancelled) setTicketRows(rows);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const { value, setValue, statusLabel } = usePersistedState(`page-builder.${canonicalEventId || id}`, {
    blocks: initialBlocks,
    active: "b1",
    device: "desktop",
    themeName: "Aivory Default",
    mediaLibrary: [e.cover],
    templateId: null,
  });

  const blocks = Array.isArray(value.blocks) ? value.blocks : initialBlocks;
  const active = value.active || "b1";
  const device = value.device || "desktop";
  const themeName = value.themeName || "Aivory Default";
  const mediaLibrary = Array.isArray(value.mediaLibrary) && value.mediaLibrary.length ? value.mediaLibrary : [e.cover];

  const setBlocks = (next, options = {}) => setValue((prev) => {
    const current = prev.blocks || initialBlocks;
    const resolved = typeof next === "function" ? next(current) : next;
    if (!options.skipHistory && JSON.stringify(current) !== JSON.stringify(resolved)) {
      historyRef.current.past = [...historyRef.current.past.slice(-19), current];
      historyRef.current.future = [];
      setHistoryTick((n) => n + 1);
    }
    return { ...prev, blocks: resolved };
  });
  const setActive = (next) => setValue((prev) => ({ ...prev, active: next }));
  const setDevice = (next) => setValue((prev) => ({ ...prev, device: next }));
  const setThemeName = (next) => setValue((prev) => ({ ...prev, themeName: next }));
  const setMediaLibrary = (next) => setValue((prev) => ({ ...prev, mediaLibrary: typeof next === "function" ? next(prev.mediaLibrary || [e.cover]) : next }));
  const templateId = value.templateId ?? null;
  const templateMeta = getTemplateMeta(templateId);

  const publishBadge = useMemo(() => {
    if (!publishedSnap?.blocks?.length) return "Draft";
    const draftFp = publishFingerprint({ blocks, themeName, templateId });
    const pubFp = publishFingerprint(publishedSnap);
    return draftFp === pubFp ? "Published" : "Unpublished changes";
  }, [blocks, themeName, templateId, publishedSnap]);

  const canUndo = historyRef.current.past.length > 0;
  const canRedo = historyRef.current.future.length > 0;
  void historyTick;

  const undoBlocks = () => {
    const past = historyRef.current.past;
    if (!past.length) return;
    const previous = past[past.length - 1];
    historyRef.current.past = past.slice(0, -1);
    historyRef.current.future = [blocks, ...historyRef.current.future].slice(0, 20);
    setBlocks(previous, { skipHistory: true });
    setHistoryTick((n) => n + 1);
  };

  const redoBlocks = () => {
    const future = historyRef.current.future;
    if (!future.length) return;
    const next = future[0];
    historyRef.current.future = future.slice(1);
    historyRef.current.past = [...historyRef.current.past, blocks].slice(-20);
    setBlocks(next, { skipHistory: true });
    setHistoryTick((n) => n + 1);
  };

  const saveSectionToLibrary = (block) => {
    if (!block) return;
    const entry = {
      id: `saved_${Date.now()}`,
      label: `${block.type} section`,
      block: { type: block.type, props: block.props || {}, visible: true },
    };
    setSavedSections((rows) => [entry, ...rows.filter((r) => r.id !== entry.id)].slice(0, 40));
    toast.success("Section saved", { description: "Find it under Saved in the section picker." });
  };

  const insertSavedSection = (entry, index = null) => {
    if (!entry?.block) return;
    const bid = `b${Date.now()}`;
    const nextBlock = { id: bid, type: entry.block.type, visible: true, props: { ...(entry.block.props || {}) } };
    if (typeof index === "number" && index >= 0 && index <= blocks.length) {
      setBlocks([...blocks.slice(0, index), nextBlock, ...blocks.slice(index)]);
    } else {
      setBlocks([...blocks, nextBlock]);
    }
    setActive(bid);
    setSectionPicker({ open: false, index: null, q: "", category: "intro", view: "grid" });
    toast.success("Section inserted");
  };

  const handleCanvasDragEnd = (dragEvent) => {
    const { active: activeId, over } = dragEvent;
    if (!over || activeId.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === activeId.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setBlocks(arrayMove(blocks, oldIndex, newIndex));
  };

  const applyPageTemplate = (tid) => {
    const meta = getTemplateMeta(tid);
    const nextBlocks = buildBlocksFromTemplate(tid, e);
    const firstId = nextBlocks[0]?.id;
    setValue((prev) => ({
      ...prev,
      blocks: nextBlocks,
      themeName: meta.themeName || prev.themeName,
      templateId: tid,
      active: firstId || prev.active,
    }));
    setTemplateModalOpen(false);
    toast.success("Layout applied", { description: meta.name });
  };

  const handleOutlineDragEnd = (dragEvent) => {
    const { active: activeId, over } = dragEvent;
    if (!over || activeId.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === activeId.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setBlocks(arrayMove(blocks, oldIndex, newIndex));
  };

  const theme = themes[themeName] || themes["Aivory Default"];
  const sel = blocks.find((b) => b.id === active);

  const move = (bid, dir) => {
    const i = blocks.findIndex((b) => b.id === bid);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    setBlocks(next);
  };

  const remove = (bid) => setBlocks((bs) => bs.filter((b) => b.id !== bid));

  const duplicate = (bid) => setBlocks((bs) => {
    const i = bs.findIndex((b) => b.id === bid);
    if (i < 0) return bs;
    const copy = { ...bs[i], id: `b${Date.now()}` };
    return [...bs.slice(0, i + 1), copy, ...bs.slice(i + 1)];
  });

  const toggleVisible = (bid) => setBlocks((bs) => bs.map((b) => (b.id === bid ? { ...b, visible: !b.visible } : b)));
  const updateProps = (key, val) => setBlocks((bs) => bs.map((b) => (b.id === active ? { ...b, props: { ...b.props, [key]: val } } : b)));

  const addBlock = (type, options = {}) => {
    const { index = null, templateProps = null } = options;
    const blockDefaults = {
      headline: { title: "Section headline", body: "Short supporting text.", align: "left" },
      list: { title: "Key points", items: ["Point one", "Point two", "Point three"] },
      text: { title: "Section title", body: "Write your content here." },
      image: { src: mediaLibrary[0] || e.cover, caption: "" },
      gallery: { images: mediaLibrary.slice(0, 3) },
      carousel: { images: mediaLibrary.slice(0, 5), autoplay: true },
      video: { title: "Watch", youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" },
      contact: { title: "Contact us", email: "hello@example.com", phone: "+65 1234 5678", cta: "Email us" },
      socials: { title: "Follow us", links: [{ label: "Instagram", url: "https://instagram.com" }, { label: "LinkedIn", url: "https://linkedin.com" }] },
      charts: { title: "Highlights", stats: [{ label: "Attendees", value: "2,400" }, { label: "Speakers", value: "72" }, { label: "NPS", value: "62" }] },
      menu: { title: "Menu", items: [{ name: "Breakfast", details: "08:00 - 09:00" }, { name: "Lunch", details: "12:00 - 13:30" }] },
      people: { title: "People", items: [{ name: "Alex", role: "Host" }, { name: "Priya", role: "Producer" }, { name: "Sam", role: "Moderator" }] },
      pricing: { title: "Pricing", plans: [{ name: "Standard", price: "$199" }, { name: "Pro", price: "$399" }, { name: "VIP", price: "$799" }] },
      accordion: { title: "Accordion", items: [{ title: "Item 1", body: "Details" }] },
      logoStrip: { logos: sponsorsData.slice(0, 6), title: "Trusted by" },
      divider: { style: "line", spacing: 40 },
      button: { label: "Learn more", href: "https://" },
      exhibitors: { title: "Exhibitors", caption: "On the show floor", items: [] },
      faq: { items: [{ q: "Question", a: "Answer" }] },
      map: { address: "89 South Street · New York, NY", embedUrl: "" },
      hero: { imageUrl: "" },
    };
    const bid = `b${Date.now()}`;
    const nextBlock = { id: bid, type, visible: true, props: { ...(blockDefaults[type] || {}), ...(templateProps || {}) } };
    if (typeof index === "number" && index >= 0 && index <= blocks.length) {
      setBlocks([...blocks.slice(0, index), nextBlock, ...blocks.slice(index)]);
    } else {
      setBlocks([...blocks, nextBlock]);
    }
    setActive(bid);
    toast.success("Block added", { description: `${type} block inserted.` });
  };

  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const data = await listMedia({ kind: "image", pageSize: 100 });
        const urls = (data.items || []).map((a) => a.url).filter(Boolean);
        if (cancel || !urls.length) return;
        setValue((prev) => {
          const prevLib = Array.isArray(prev.mediaLibrary) ? prev.mediaLibrary : [];
          const kept = prevLib.filter((u) => u && !String(u).startsWith("data:"));
          const next = [...new Set([...urls, ...kept])];
          // Avoid rewriting state (and re-triggering autosave) when nothing changed.
          if (next.length === kept.length && next.every((u, i) => u === kept[i])) return prev;
          return { ...prev, mediaLibrary: next };
        });
      } catch {
        /* library optional on load */
      }
    })();
    return () => { cancel = true; };
    // Load once per event — do not depend on setMediaLibrary (unstable) or loop forever.
  }, [id, setValue]);

  const uploadMediaFiles = async (files) => {
    if (!files?.length) return;
    const uploaded = [];
    for (const file of Array.from(files)) {
      try {
        const data = await uploadMediaFile(file);
        if (data?.asset?.url) uploaded.push(data.asset.url);
      } catch (err) {
        toast.error(err?.message || "Upload failed");
      }
    }
    if (!uploaded.length) return;
    setMediaLibrary((prev) => [...new Set([...(prev || []), ...uploaded])]);
    if (sel?.type === "hero" && uploaded[0]) {
      updateProps("imageUrl", uploaded[0]);
      patchEventCover(resolvedEventId, uploaded[0]).then((ok) => {
        if (ok) refreshEvents();
      });
    }
    toast.success("Media uploaded", { description: `${uploaded.length} file(s) added to workspace library.` });
  };

  const dev = devices.find((d) => d.id === device) || devices[0];
  const sourceHint = sel ? SOURCE_HINTS[sel.type] || "Editable block." : "Select a block to see source details.";
  const openBlockInsert = (type, index = null) => {
    if (GENERIC_BLOCK_TEMPLATES[type]?.length) {
      setGenericPicker({ open: true, type, index, replaceId: null });
      return;
    }
    addBlock(type, { index });
  };
  const openAlternateLayouts = (block) => {
    const templates = GENERIC_BLOCK_TEMPLATES[block.type] || [];
    if (!templates.length) {
      toast.message("No alternate layout", { description: `No alternate templates for ${block.type} yet.` });
      return;
    }
    setGenericPicker({
      open: true,
      type: block.type,
      index: blocks.findIndex((row) => row.id === block.id),
      replaceId: block.id,
    });
  };
  const openSectionPicker = (index) => setSectionPicker({ open: true, index, q: "", category: "intro", view: "grid" });

  const sectionPickerItems = useMemo(() => {
    const q = sectionPicker.q.trim().toLowerCase();
    let items = sectionTypesForCategory(sectionPicker.category);
    if (q) {
      items = SECTION_CATEGORY_GROUPS.flatMap((g) => g.categories).flatMap((c) => c.types).filter((item) => {
        const meta = blockMetaForType(item.type);
        return meta.label.toLowerCase().includes(q) || item.type.toLowerCase().includes(q) || (meta.body || "").toLowerCase().includes(q);
      });
    }
    return items;
  }, [sectionPicker.category, sectionPicker.q]);

  useEffect(() => {
    const families = new Set();
    GOOGLE_FONTS.forEach((font) => {
      if ((previewFont || "").includes(font.label)) families.add(font.family);
    });
    blocks.forEach((b) => {
      const ff = String(b?.props?.fontFamily || "");
      const matched = GOOGLE_FONTS.find((f) => ff.includes(f.label));
      if (matched) families.add(matched.family);
    });
    if (!families.size) return;
    const linkId = "page-builder-google-fonts";
    let link = document.getElementById(linkId);
    const href = `https://fonts.googleapis.com/css2?${Array.from(families).map((f) => `family=${f}`).join("&")}&display=swap`;
    if (!link) {
      link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = href;
  }, [blocks, previewFont]);

  return (
    <div className="h-full min-h-0 flex flex-col bg-[var(--bg-base)]" data-testid="page-builder">
      <input ref={mediaInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(ev) => uploadMediaFiles(ev.target.files)} />

      {/* Editor chrome — Squarespace-style slim toolbar */}
      <header className="relative shrink-0 h-14 border-b border-[var(--border-default)] bg-[var(--bg-base)] px-4 md:px-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <Link
            to={`/admin/events/${id}`}
            className="inline-flex items-center gap-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors px-2 py-1.5 rounded-md hover:bg-[var(--bg-surface)]"
            data-testid="page-builder-exit"
          >
            <ArrowLeft size={15} />
            <span className="hidden sm:inline">Exit</span>
          </Link>
          <button
            type="button"
            onClick={() => toast.success("Draft saved", { description: "Changes save automatically." })}
            className="text-sm px-3 py-1.5 rounded-md border border-transparent text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] hover:border-[var(--border-default)] transition-colors"
            data-testid="save-draft-page"
          >
            {statusLabel === "Saved" ? "Saved" : statusLabel || "Save"}
          </button>
        </div>

        <div className="absolute left-1/2 -translate-x-1/2 text-center pointer-events-none hidden sm:block">
          <div className="text-sm font-medium text-[var(--text-primary)] truncate max-w-[220px] md:max-w-[320px]">
            {e.title || "Landing page"}
          </div>
          <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5">
            Landing · {publishBadge}
            {statusLabel === "Saved" ? "" : ` · ${statusLabel || "Saving…"}`}
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <div className="hidden sm:flex items-center border border-[var(--border-default)] rounded-lg overflow-hidden bg-[var(--bg-surface)]">
            {devices.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setDevice(d.id)}
                data-testid={`device-${d.id}`}
                title={d.id}
                className={`p-2 transition-colors ${device === d.id ? "bg-[var(--bg-elev)] text-[var(--text-primary)]" : "text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"}`}
              >
                <d.icon size={15} />
              </button>
            ))}
          </div>
          {!previewMode && editMode ? (
            <button
              type="button"
              className={`hidden md:inline-flex p-2 rounded-lg border transition-colors ${sectionsPanelOpen ? "border-[var(--text-primary)] text-[var(--text-primary)] bg-[var(--bg-surface)]" : "border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
              onClick={() => setSectionsPanelOpen((v) => !v)}
              title="Sections"
            >
              <Layers size={15} />
            </button>
          ) : null}
          {!previewMode && editMode ? (
            <div className="hidden md:flex items-center gap-0.5 border border-[var(--border-default)] rounded-lg overflow-hidden">
              <button type="button" className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-30" onClick={undoBlocks} disabled={!canUndo} title="Undo">
                <Undo2 size={15} />
              </button>
              <button type="button" className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-30" onClick={redoBlocks} disabled={!canRedo} title="Redo">
                <Redo2 size={15} />
              </button>
            </div>
          ) : null}
          <div className="relative">
            <button
              type="button"
              className={`p-2 rounded-lg border transition-colors ${stylesPanelOpen ? "border-[var(--text-primary)] text-[var(--text-primary)] bg-[var(--bg-surface)]" : "border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
              onClick={() => setStylesPanelOpen((v) => !v)}
              title="Site styles"
              data-testid="page-builder-styles"
            >
              <Brush size={15} />
            </button>
            {stylesPanelOpen ? (
              <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-72 rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-xl p-4 space-y-3">
                <div className="text-xs font-medium text-[var(--text-primary)]">Page styles</div>
                <button type="button" className="btn-secondary w-full justify-center text-xs" onClick={() => { setTemplateModalOpen(true); setStylesPanelOpen(false); }}>
                  <LayoutTemplate size={13} /> Change layout
                </button>
                <label className="block text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">Color theme</label>
                <select value={themeName} onChange={(ev) => setThemeName(ev.target.value)} className="input-dark text-sm w-full" data-testid="theme-select">
                  {Object.keys(themes).map((t) => <option key={t}>{t}</option>)}
                </select>
                <label className="block text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">Preview font</label>
                <select value={previewFont} onChange={(ev) => setPreviewFont(ev.target.value)} className="input-dark text-sm w-full">
                  <option value='"DM Sans", system-ui, sans-serif'>DM Sans</option>
                  <option value='Georgia, "Times New Roman", serif'>Georgia</option>
                  <option value='ui-monospace, monospace'>Mono</option>
                </select>
                <label className="block text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">Base size ({previewSizePx}px)</label>
                <input type="range" min={12} max={22} value={previewSizePx} onChange={(ev) => setPreviewSizePx(Number(ev.target.value) || 16)} className="w-full accent-[var(--primary)]" />
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="p-2 rounded-lg border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            onClick={() => {
              if (!editMode) {
                setEditMode(true);
                setPreviewMode(false);
              } else {
                setPreviewMode((v) => !v);
              }
            }}
            title={previewMode ? "Exit preview" : "Preview"}
          >
            {previewMode ? <EyeOff size={15} /> : <Play size={15} />}
          </button>
          <Link to={`/e/${e.slug}`} target="_blank" className="p-2 rounded-lg border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors hidden md:inline-flex" title="Open live page">
            <ExternalLink size={15} />
          </Link>
          <button
            type="button"
            onClick={async () => {
              try {
                const key = `page-builder-published.${canonicalEventId}`;
                const publishedValue = {
                  blocks,
                  themeName,
                  templateId,
                  mediaLibrary,
                  previewFont,
                  previewSizePx,
                  publishedAt: new Date().toISOString(),
                };
                const res = await fetch(`${API_BASE}/api/state/${encodeURIComponent(key)}`, {
                  method: "PUT",
                  headers: { "content-type": "application/json", ...getAuthHeaders() },
                  body: JSON.stringify({ value: publishedValue }),
                });
                if (!res.ok) throw new Error("publish failed");
                setPublishedSnap(publishedValue);
                toast.success("Page published", { description: "Public page is now live." });
              } catch {
                toast.error("Publish failed", { description: "Could not save published page state." });
              }
            }}
            className="btn-primary text-sm !py-2 !px-4 ml-1"
            data-testid="publish-page"
          >
            Publish
          </button>
        </div>
      </header>

      <div className={`flex-1 min-h-0 flex overflow-hidden ${previewMode ? "bg-[#e8e8ea] dark:bg-[var(--bg-elev)]" : ""}`}>
        {templateModalOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/70 backdrop-blur-md" role="dialog" aria-modal="true" aria-labelledby="template-picker-title">
            <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] shadow-[0_40px_120px_rgba(0,0,0,0.55)]">
              <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-[var(--border-default)] bg-[var(--bg-base)]/95 backdrop-blur px-8 py-6">
                <div>
                  <div id="template-picker-title" className="font-heading text-2xl tracking-tight text-[var(--text-primary)]">Layouts</div>
                  <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-xl">Pick a prebuilt conference or expo structure—or start from a blank canvas. You can still edit every block after.</p>
                </div>
                <button type="button" className="btn-ghost p-2 rounded-xl border border-[var(--border-default)]" onClick={() => setTemplateModalOpen(false)} aria-label="Close"><X size={18} /></button>
              </div>
              <div className="p-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {PAGE_BUILDER_TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => applyPageTemplate(t.id)}
                    data-testid={`template-${t.id}`}
                    className="group text-left rounded-2xl border border-[var(--border-default)] overflow-hidden bg-[var(--bg-elev)] hover:border-[#3f3f46] hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/40"
                  >
                    <div className="h-28 relative" style={{ background: t.preview }}>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <span className="absolute bottom-3 left-4 text-[10px] font-mono uppercase tracking-widest text-white/80">{t.category}</span>
                      <span className="absolute top-3 right-4 w-3 h-3 rounded-full shadow-lg" style={{ background: t.accent, boxShadow: `0 0 20px ${t.accent}66` }} />
                    </div>
                    <div className="p-5">
                      <div className="font-medium text-[var(--text-primary)] group-hover:text-[var(--primary)] transition-colors">{t.name}</div>
                      <p className="text-xs text-[var(--text-tertiary)] mt-2 leading-relaxed">{t.blurb}</p>
                      <div className="mt-4 text-[10px] font-mono uppercase tracking-wider text-[#52525B]">Theme · {t.themeName}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {sectionPicker.open ? (
          <div className="fixed inset-0 z-50 flex bg-white dark:bg-[var(--bg-base)]" role="dialog" aria-modal="true" aria-labelledby="add-section-title">
            {/* Sidebar */}
            <aside className="hidden md:flex w-[220px] shrink-0 flex-col border-r border-[#e4e4e7] dark:border-[var(--border-default)] bg-white dark:bg-[var(--bg-base)]">
              <button
                type="button"
                className="text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--text-secondary)] px-5 pt-5 pb-3 hover:text-[var(--text-primary)]"
                onClick={() => setSectionPicker({ open: false, index: null, q: "", category: "intro", view: "grid" })}
              >
                Close
              </button>
              <h2 id="add-section-title" className="px-5 text-xl font-semibold text-[var(--text-primary)] mb-4">Add a section</h2>
              <button
                type="button"
                className="mx-4 mb-4 text-left text-sm text-[var(--text-primary)] hover:underline flex items-center gap-2"
                onClick={() => {
                  addBlock("text", { index: sectionPicker.index, templateProps: { title: "New section", body: "" } });
                  setSectionPicker({ open: false, index: null, q: "", category: "intro", view: "grid" });
                }}
              >
                <Plus size={14} /> Add blank
              </button>
              <div className="px-5 py-2 text-sm text-[var(--text-tertiary)] flex items-center gap-2 border-t border-[#f0f0f2] dark:border-[var(--border-default)]">
                <Heart size={14} /> Saved
              </div>
              {savedSections.length ? (
                <div className="px-3 pb-3 space-y-1 max-h-40 overflow-y-auto">
                  {savedSections.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-[var(--bg-surface)] text-[var(--text-secondary)]"
                      onClick={() => insertSavedSection(entry, sectionPicker.index)}
                    >
                      {entry.label}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="px-5 pb-3 text-[11px] text-[var(--text-tertiary)]">Save a section from the canvas menu.</p>
              )}
              <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
                {SECTION_CATEGORY_GROUPS.map((group) => (
                  <div key={group.label}>
                    <div className="px-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)] mb-2">{group.label}</div>
                    <div className="space-y-0.5">
                      {group.categories.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          className={`w-full text-left px-2 py-1.5 text-sm rounded transition-colors ${sectionPicker.category === cat.id ? "text-[var(--text-primary)] underline underline-offset-4" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
                          onClick={() => setSectionPicker((s) => ({ ...s, category: cat.id, q: "" }))}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </aside>

            {/* Main panel */}
            <div className="flex-1 min-w-0 flex flex-col">
              <div className="md:hidden flex items-center justify-between px-4 h-14 border-b border-[#e4e4e7] dark:border-[var(--border-default)]">
                <button type="button" className="text-sm font-medium" onClick={() => setSectionPicker({ open: false, index: null, q: "", category: "intro", view: "grid" })}>Close</button>
                <span className="font-semibold">Add a section</span>
                <div className="w-12" />
              </div>
              <div className="flex items-center gap-3 px-4 md:px-8 py-3 border-b border-[#f0f0f2] dark:border-[var(--border-default)]">
                <input
                  className="flex-1 max-w-md text-sm px-3 py-2 rounded-md border border-[#e4e4e7] dark:border-[var(--border-default)] bg-white dark:bg-[var(--bg-surface)] focus:outline-none focus:border-[#1a73e8]"
                  placeholder="Search all sections…"
                  value={sectionPicker.q}
                  onChange={(ev) => setSectionPicker((s) => ({ ...s, q: ev.target.value }))}
                />
                <div className="flex items-center border border-[#e4e4e7] dark:border-[var(--border-default)] rounded-md overflow-hidden shrink-0">
                  <button
                    type="button"
                    className={`p-2 ${sectionPicker.view === "grid" ? "bg-[#f4f4f5] dark:bg-[var(--bg-elev)] text-[var(--text-primary)]" : "text-[var(--text-tertiary)]"}`}
                    onClick={() => setSectionPicker((s) => ({ ...s, view: "grid" }))}
                    aria-label="Grid view"
                  >
                    <LayoutGrid size={16} />
                  </button>
                  <button
                    type="button"
                    className={`p-2 ${sectionPicker.view === "list" ? "bg-[#f4f4f5] dark:bg-[var(--bg-elev)] text-[var(--text-primary)]" : "text-[var(--text-tertiary)]"}`}
                    onClick={() => setSectionPicker((s) => ({ ...s, view: "list" }))}
                    aria-label="List view"
                  >
                    <List size={16} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-[#fafafa] dark:bg-[var(--bg-elev)]">
                {sectionPicker.view === "grid" ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl">
                    {sectionPickerItems.map((item, idx) => {
                      const meta = blockMetaForType(item.type);
                      return (
                        <button
                          key={`${item.type}-${item.thumb}-${idx}`}
                          type="button"
                          className="text-left bg-white dark:bg-[var(--bg-surface)] border border-[#e4e4e7] dark:border-[var(--border-default)] rounded-sm overflow-hidden hover:border-[#1a73e8] hover:shadow-[0_4px_20px_rgba(26,115,232,0.1)] transition-all focus:outline-none focus:ring-2 focus:ring-[#1a73e8]/25"
                          onClick={() => {
                            openBlockInsert(item.type, sectionPicker.index);
                            setSectionPicker({ open: false, index: null, q: "", category: "intro", view: "grid" });
                          }}
                        >
                          <SectionTemplateThumb thumb={item.thumb || item.type} type={item.type} />
                          <div className="px-4 py-2 border-t border-[#f0f0f2] dark:border-[var(--border-default)]">
                            <span className="text-xs text-[var(--text-tertiary)]">{meta.label}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="max-w-2xl space-y-2">
                    {sectionPickerItems.map((item, idx) => {
                      const meta = blockMetaForType(item.type);
                      const Icon = meta.icon || Type;
                      return (
                        <button
                          key={`${item.type}-list-${idx}`}
                          type="button"
                          className="w-full flex items-center gap-4 p-3 bg-white dark:bg-[var(--bg-surface)] border border-[#e4e4e7] dark:border-[var(--border-default)] rounded-md hover:border-[#1a73e8] text-left transition-colors"
                          onClick={() => {
                            openBlockInsert(item.type, sectionPicker.index);
                            setSectionPicker({ open: false, index: null, q: "", category: "intro", view: "grid" });
                          }}
                        >
                          <div className="w-20 h-14 shrink-0 overflow-hidden rounded border border-[#eee] dark:border-[var(--border-default)]">
                            <SectionTemplateThumb thumb={item.thumb || item.type} type={item.type} compact />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
                              <Icon size={14} className="text-[var(--text-tertiary)]" />
                              {meta.label}
                            </div>
                            <p className="text-xs text-[var(--text-tertiary)] mt-0.5 truncate">{meta.body}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {sectionPickerItems.length === 0 ? (
                  <div className="text-sm text-[var(--text-tertiary)] py-12 text-center">No sections match your search.</div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {genericPicker.open ? (
          <div className="fixed inset-0 z-50 flex flex-col bg-[#f4f4f5] dark:bg-[var(--bg-elev)]" role="dialog" aria-modal="true" aria-labelledby="layout-picker-title">
            <div className="relative shrink-0 flex items-center justify-between px-4 md:px-6 h-14 border-b border-[#e4e4e7] dark:border-[var(--border-default)] bg-white dark:bg-[var(--bg-base)]">
              <button
                type="button"
                className="p-2 rounded-md text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)]"
                onClick={() => setGenericPicker({ open: false, type: null, index: null, replaceId: null })}
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <h2 id="layout-picker-title" className="absolute left-1/2 -translate-x-1/2 text-base font-semibold text-[var(--text-primary)]">
                Layouts
              </h2>
              <div className="w-9" aria-hidden />
            </div>
            <div className="flex-1 overflow-y-auto py-8 px-4 md:px-10">
              <div className="max-w-3xl mx-auto space-y-5">
                {(GENERIC_BLOCK_TEMPLATES[genericPicker.type] || []).map((t, idx) => (
                  <button
                    key={`${genericPicker.type}-${idx}`}
                    type="button"
                    className="w-full text-left bg-white dark:bg-[var(--bg-surface)] border border-[#e4e4e7] dark:border-[var(--border-default)] rounded-sm hover:border-[#1a73e8] hover:shadow-[0_4px_24px_rgba(26,115,232,0.12)] transition-all focus:outline-none focus:ring-2 focus:ring-[#1a73e8]/30 overflow-hidden"
                    onClick={() => {
                      if (genericPicker.replaceId) {
                        setBlocks((current) =>
                          current.map((row) =>
                            row.id === genericPicker.replaceId
                              ? { ...row, props: { ...(row.props || {}), ...t.props } }
                              : row,
                          ),
                        );
                        setActive(genericPicker.replaceId);
                        toast.success("Layout updated");
                      } else {
                        addBlock(genericPicker.type, { index: genericPicker.index, templateProps: t.props });
                      }
                      setGenericPicker({ open: false, type: null, index: null, replaceId: null });
                    }}
                  >
                    <div className="px-6 py-8 md:px-10 md:py-12">
                      <LayoutVariantPreview
                        type={genericPicker.type}
                        layout={t.layout || layoutForTemplate(genericPicker.type, idx)}
                        props={t.props}
                      />
                    </div>
                    <div className="px-6 py-2 border-t border-[#f0f0f2] dark:border-[var(--border-default)] bg-[#fafafa] dark:bg-[var(--bg-elev)]">
                      <span className="text-xs text-[var(--text-tertiary)]">{t.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {!previewMode && editMode && sectionsPanelOpen ? (
        <aside className="hidden md:flex w-[280px] shrink-0 border-r border-[var(--border-default)] flex-col overflow-hidden bg-[var(--bg-base)]">
          <div className="px-4 py-3 border-b border-[var(--border-default)] flex items-center justify-between">
            <div className="text-sm font-medium text-[var(--text-primary)]">Sections</div>
            <button type="button" className="p-1.5 rounded-md text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)]" onClick={() => setSectionsPanelOpen(false)} aria-label="Close sections">
              <PanelLeftClose size={14} />
            </button>
          </div>
          <div className="p-4 border-b border-[var(--border-default)] space-y-2 overflow-y-auto max-h-[40vh]">
            <button type="button" className="btn-secondary w-full justify-center text-xs" onClick={() => openSectionPicker(blocks.length)}>
              <Plus size={12} /> Add section
            </button>
            <AccordionGroup
              title="Event blocks"
              open={openGroup === "prefilled"}
              onToggle={() => setOpenGroup((g) => (g === "prefilled" ? "none" : "prefilled"))}
              blocks={PREFILLED_BLOCKS}
              addBlock={(type) => openBlockInsert(type)}
            />
            <AccordionGroup
              title="Content blocks"
              open={openGroup === "generic"}
              onToggle={() => setOpenGroup((g) => (g === "generic" ? "none" : "generic"))}
              blocks={GENERIC_BLOCKS}
              addBlock={(type) => openBlockInsert(type)}
            />
          </div>

          <div className="p-4 border-b border-[var(--border-default)]">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium text-[var(--text-secondary)]">Media</div>
              <div className="flex gap-1">
                <button type="button" className="text-xs text-[var(--text-tertiary)] hover:text-[var(--primary)]" onClick={() => setMediaLibraryOpen(true)}>Browse</button>
                <button type="button" className="text-xs text-[var(--text-tertiary)] hover:text-[var(--primary)]" onClick={() => mediaInputRef.current?.click()}>Upload</button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1.5 max-h-32 overflow-y-auto">
              {mediaLibrary.slice(0, 16).map((m, i) => (
                <button
                  key={`${String(m).slice(0, 30)}-${i}`}
                  className="aspect-square border border-[var(--border-default)] overflow-hidden rounded-md"
                  onClick={() => {
                    if (!sel) return;
                    if (sel.type === "hero") updateProps("imageUrl", m);
                    if (sel.type === "image") updateProps("src", m);
                    if (sel.type === "gallery") {
                      const cur = Array.isArray(sel.props?.images) ? sel.props.images : [];
                      updateProps("images", [...cur.slice(0, 5), m]);
                    }
                  }}
                  title="Use in selected block"
                >
                  <img src={resolveMediaUrl(m)} alt="asset" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOutlineDragEnd}>
              <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-1">
                  {blocks.map((b) => (
                    <SortableOutlineRow
                      key={b.id}
                      block={b}
                      active={active === b.id}
                      onActivate={() => setActive(b.id)}
                      move={move}
                      meta={BLOCK_INDEX.find((l) => l.type === b.type)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </aside>
        ) : null}

        <main className={`flex-1 min-w-0 overflow-y-auto flex flex-col items-center py-8 px-4 md:px-10 ${previewMode ? "bg-[#e8e8ea] dark:bg-[var(--bg-elev)]" : "bg-[#ececee] dark:bg-[var(--bg-elev)]"}`}>
          <div
            className="transition-all rounded-sm ring-1 ring-black/5 shadow-[0_8px_40px_rgba(0,0,0,0.12)]"
            style={{
              width: dev.w,
              maxWidth: "100%",
              background: theme.bg,
              color: "#FAFAFA",
              fontFamily: previewFont,
              fontSize: previewSizePx,
            }}
          >
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCanvasDragEnd}>
              <SortableContext items={blocks.filter((b) => b.visible).map((b) => b.id)} strategy={verticalListSortingStrategy}>
            {blocks.filter((b) => b.visible).map((b, visibleIdx, visibleRows) => (
              <SortableCanvasSection key={b.id} id={b.id}>
              {(dragListeners) => (
              <div className="relative group/section">
                {editMode && !previewMode ? (
                  <InsertBetween
                    onOpen={() => openSectionPicker(blocks.findIndex((row) => row.id === b.id))}
                  />
                ) : null}
                {editMode && !previewMode ? (
                  <div className="absolute top-2 left-2 z-20 opacity-0 group-hover/section:opacity-100 focus-within:opacity-100 transition-opacity">
                    <button
                      type="button"
                      className="touch-none cursor-grab active:cursor-grabbing p-1.5 rounded-md border border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-primary)] shadow"
                      aria-label="Drag to reorder section"
                      {...dragListeners}
                    >
                      <GripVertical size={14} className="text-[var(--text-tertiary)]" />
                    </button>
                  </div>
                ) : null}
                {editMode && !previewMode ? (
                  <div className="absolute top-2 right-2 z-20 opacity-0 group-hover/section:opacity-100 focus-within:opacity-100 transition-opacity">
                    {/* Explicit chrome colors — parent canvas forces color:#FAFAFA which washed out this menu */}
                    <div className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-lg p-1 min-w-[130px]">
                      <button
                        type="button"
                        className="w-full text-left text-xs px-2.5 py-2 rounded-md text-[var(--text-primary)] hover:bg-[var(--bg-elev)]"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setActive(b.id);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="w-full text-left text-xs px-2.5 py-2 rounded-md text-[var(--text-primary)] hover:bg-[var(--bg-elev)] disabled:opacity-40"
                        disabled={!((GENERIC_BLOCK_TEMPLATES[b.type] || []).length)}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          openAlternateLayouts(b);
                        }}
                      >
                        Layouts
                      </button>
                      <button
                        type="button"
                        className="w-full text-left text-xs px-2.5 py-2 rounded-md text-[var(--text-primary)] hover:bg-[var(--bg-elev)] flex items-center gap-1.5"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          saveSectionToLibrary(b);
                        }}
                      >
                        <Heart size={12} /> Save section
                      </button>
                      <div className="border-t border-[var(--border-default)] my-1" />
                      <div className="flex gap-0.5 px-1 pb-1">
                        <button type="button" className="flex-1 text-xs px-2 py-1.5 rounded text-[var(--text-primary)] hover:bg-[var(--bg-elev)]" onClick={(ev) => { ev.stopPropagation(); move(b.id, -1); }}>↑</button>
                        <button type="button" className="flex-1 text-xs px-2 py-1.5 rounded text-[var(--text-primary)] hover:bg-[var(--bg-elev)]" onClick={(ev) => { ev.stopPropagation(); move(b.id, 1); }}>↓</button>
                      </div>
                    </div>
                  </div>
                ) : null}
                <BlockRender
                  block={b}
                  theme={theme}
                  active={active === b.id}
                  onClick={() => {
                    if (!editMode) return;
                    setActive(b.id);
                  }}
                  event={e}
                  speakersPreview={speakersPreview}
                  sessionsData={sessionsData}
                  sponsorsData={sponsorsData}
                  exhibitorsData={exhibitorsData}
                  ticketRows={ticketRows}
                  mediaLibrary={mediaLibrary}
                  onInlineEdit={(key, val) => {
                    if (!editMode) return;
                    setActive(b.id);
                    setBlocks((current) => current.map((x) => (x.id === b.id ? { ...x, props: { ...(x.props || {}), [key]: val } } : x)));
                  }}
                />
                {editMode && !previewMode && visibleIdx === visibleRows.length - 1 ? (
                  <InsertBetween
                    onOpen={() => openSectionPicker(blocks.length)}
                  />
                ) : null}
              </div>
              )}
              </SortableCanvasSection>
            ))}
              </SortableContext>
            </DndContext>
          </div>
        </main>

        {!previewMode && editMode && sel ? (
          <aside className="w-full md:w-[320px] shrink-0 border-l border-[var(--border-default)] bg-[var(--bg-base)] flex flex-col overflow-hidden fixed inset-x-0 bottom-0 z-40 max-h-[55vh] md:static md:max-h-none md:z-auto">
            <div className="px-4 pt-3 border-b border-[var(--border-default)] shrink-0">
              <div className="flex items-center gap-1">
                {[
                  { id: "content", label: "Content" },
                  { id: "design", label: "Design" },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`text-sm px-3 py-2.5 border-b-2 transition-colors ${inspectorTab === tab.id ? "border-[var(--text-primary)] text-[var(--text-primary)]" : "border-transparent text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"}`}
                    onClick={() => setInspectorTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
                <button
                  type="button"
                  className="ml-auto p-2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  onClick={() => setActive(null)}
                  aria-label="Close inspector"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
              {inspectorTab === "content" ? (
                <PropEditor
                  block={sel}
                  update={updateProps}
                  mediaLibrary={mediaLibrary}
                  onUpload={() => mediaInputRef.current?.click()}
                  onOpenLibrary={() => setMediaLibraryOpen(true)}
                />
              ) : null}
              {inspectorTab === "design" ? (
                <div className="space-y-3">
                  <Field label="Font family">
                    <select className="input-dark" value={sel.props?.fontFamily || ""} onChange={(ev) => updateProps("fontFamily", ev.target.value)}>
                      <option value="">Inherit page font</option>
                      {GOOGLE_FONTS.map((f) => (
                        <option key={f.label} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Text color">
                    <input type="color" value={sel.props?.textColor || "#FAFAFA"} onChange={(ev) => updateProps("textColor", ev.target.value)} />
                  </Field>
                  <Field label="Accent color">
                    <input type="color" value={sel.props?.accentColor || theme.accent} onChange={(ev) => updateProps("accentColor", ev.target.value)} />
                  </Field>
                </div>
              ) : null}
              <div className="pt-2 border-t border-[var(--border-default)] flex gap-2">
                <button onClick={() => duplicate(sel.id)} className="btn-secondary text-xs flex-1 justify-center"><CopyIcon size={12} /> Duplicate</button>
                <button onClick={() => remove(sel.id)} className="btn-secondary text-xs flex-1 justify-center text-[#FF453A] border-[#FF453A]/40 hover:border-[#FF453A]"><Trash2 size={12} /> Remove</button>
              </div>
            </div>
          </aside>
        ) : null}
      </div>

      <MediaPickerModal
        open={mediaLibraryOpen}
        onClose={() => setMediaLibraryOpen(false)}
        kind="image"
        title="Workspace media"
        onSelect={(asset) => {
          const url = asset?.url;
          if (!url) return;
          setMediaLibrary((prev) => [...new Set([...(prev || []), url])]);
          if (sel?.type === "hero") {
            updateProps("imageUrl", url);
            patchEventCover(resolvedEventId, url)
              .then((ok) => {
                if (ok) refreshEvents();
              })
              .catch(() => {});
          } else if (sel?.type === "image") updateProps("src", url);
          else if (sel?.type === "gallery") {
            const cur = Array.isArray(sel.props?.images) ? sel.props.images : [];
            updateProps("images", [...cur.slice(0, 5), url]);
          }
        }}
      />
    </div>
  );
}

function SortableCanvasSection({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 30 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children(listeners)}
    </div>
  );
}

function SortableOutlineRow({ block, active, onActivate, move, meta }) {
  const Icon = meta?.icon || Type;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 20 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`outline-${block.id}`}
      className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-colors ${active ? "bg-[var(--bg-surface)] border-[var(--text-primary)]/30 text-[var(--text-primary)]" : "border-transparent hover:border-[var(--border-default)] hover:bg-[var(--bg-surface)] text-[var(--text-secondary)]"}`}
    >
      <button
        type="button"
        className="touch-none cursor-grab active:cursor-grabbing p-1 rounded hover:bg-[var(--bg-elev)] shrink-0"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder section"
        onClick={(ev) => ev.stopPropagation()}
      >
        <GripVertical size={12} className="opacity-40" />
      </button>
      <div role="button" tabIndex={0} onClick={() => onActivate()} onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onActivate(); } }} className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
        <Icon size={13} />
        <span className="text-xs flex-1 truncate capitalize">{meta?.label || block.type}</span>
        {!block.visible && <EyeOff size={11} className="opacity-50 shrink-0" />}
      </div>
    </div>
  );
}

function AccordionGroup({ title, open, onToggle, blocks, addBlock }) {
  return (
    <div className="rounded-lg border border-[var(--border-default)] overflow-hidden">
      <button onClick={onToggle} className="w-full px-3 py-2 flex items-center justify-between text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]">
        <span>{title}</span>
        {open ? <PanelTopOpen size={12} /> : <PanelTopClose size={12} />}
      </button>
      {open ? (
        <div className="grid grid-cols-2 gap-1.5 p-2 border-t border-[var(--border-default)] bg-[var(--bg-surface)]/50">
          {blocks.map((l) => (
            <button key={l.type} onClick={() => addBlock(l.type)} data-testid={`add-block-${l.type}`} className="border border-[var(--border-default)] rounded-md p-2 text-left hover:border-[var(--text-tertiary)] transition-colors">
              <l.icon size={13} className="text-[var(--text-tertiary)]" />
              <div className="text-xs font-medium mt-1.5 text-[var(--text-primary)]">{l.label}</div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function InsertBetween({ onOpen }) {
  return (
    <div className="relative z-20 flex items-center justify-center min-h-[10px] group-hover/section:min-h-[40px] transition-[min-height] duration-150 ease-out">
      <div
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-0.5 bg-[#1a73e8] opacity-0 group-hover/section:opacity-100 transition-opacity pointer-events-none"
        aria-hidden
      />
      <button
        type="button"
        onClick={onOpen}
        className="relative z-10 opacity-0 group-hover/section:opacity-100 focus:opacity-100 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-white bg-[#1a73e8] rounded hover:bg-[#1557b0] active:bg-[#1248a0] shadow-[0_2px_8px_rgba(26,115,232,0.35)] transition-all duration-150"
      >
        Add section
      </button>
    </div>
  );
}

function SectionTemplateThumb({ thumb, type, compact = false }) {
  const h = compact ? "h-full min-h-[56px]" : "h-[200px] md:h-[220px]";
  const title = "Big Ideas, Real Impact.";
  const sub = "A short line that supports your headline.";
  const key = thumb || type;

  const wrap = (children, className = "") => (
    <div
      className={`${h} w-full overflow-hidden pointer-events-none select-none bg-white text-[#111] ${className}`}
      data-testid={`section-thumb-${key}`}
    >
      {children}
    </div>
  );

  if (key === "hero-full" || type === "hero") {
    return wrap(
      <div className="relative h-full bg-gradient-to-br from-[#6b5b4f] via-[#8a7060] to-[#4a3f38] flex items-center justify-center p-6">
        <div className="text-center text-white">
          <div className={`font-serif leading-tight ${compact ? "text-[9px]" : "text-lg md:text-xl"}`}>{title}</div>
          {!compact ? <p className="text-[11px] text-white/80 mt-2 max-w-[200px] mx-auto">{sub}</p> : null}
          {!compact ? <div className="inline-block mt-4 bg-[#111] text-white text-[10px] px-4 py-1.5">Get started</div> : null}
        </div>
      </div>,
    );
  }

  if (key === "headline-split" || key === "about-text") {
    return wrap(
      <div className="h-full flex">
        <div className="w-[38%] bg-gradient-to-br from-[#d4cfc8] to-[#a89f94]" />
        <div className="flex-1 p-4 flex flex-col justify-center">
          <div className={`font-serif leading-snug ${compact ? "text-[8px]" : "text-sm md:text-base"}`}>Start your next adventure</div>
          {!compact ? <p className="text-[10px] text-[#666] mt-2 leading-relaxed line-clamp-3">{sub}</p> : null}
        </div>
      </div>,
    );
  }

  if (key === "cta-dark") {
    return wrap(
      <div className="h-full bg-[#111] text-white flex items-center justify-between px-5 gap-3">
        <div className={`font-serif leading-tight ${compact ? "text-[8px]" : "text-sm md:text-base"}`}>{title}</div>
        {!compact ? <span className="shrink-0 bg-[#FF5608] text-white text-[9px] font-bold px-3 py-1.5 uppercase">Get a quote</span> : null}
      </div>,
    );
  }

  if (key === "text-accent" || key === "headline-centered") {
    return wrap(
      <div className="h-full flex items-center px-5 gap-4 relative overflow-hidden">
        <div className="flex-1 min-w-0">
          <div className={`font-serif leading-tight ${compact ? "text-[8px]" : "text-sm md:text-base"}`}>{title}</div>
          {!compact ? <p className="text-[10px] text-[#666] mt-2 line-clamp-2">{sub}</p> : null}
        </div>
        {!compact ? (
          <div className="w-16 h-16 shrink-0 rounded-full bg-[#FF5608]/20 border-2 border-[#FF5608]/50 flex items-center justify-center text-[9px] text-[#FF5608] font-bold rotate-12">
            NEW
          </div>
        ) : null}
      </div>,
    );
  }

  if (key === "image-inline" || type === "image") {
    return wrap(
      <div className="h-full flex flex-col">
        <div className="flex-1 bg-gradient-to-br from-[#c7d2e0] via-[#9aa8bc] to-[#6b7c93] relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`${compact ? "w-6 h-6" : "w-10 h-10"} rounded-full border-2 border-white/80`} />
          </div>
        </div>
        {!compact ? (
          <div className="px-3 py-2 border-t border-[#eee]">
            <div className="h-2 w-1/3 bg-[#ddd] rounded" />
          </div>
        ) : null}
      </div>,
    );
  }

  if (key === "speakers" || key === "people-grid") {
    return wrap(
      <div className="h-full p-4 flex flex-col justify-center">
        <div className={`text-center font-serif mb-3 ${compact ? "text-[8px]" : "text-sm"}`}>Meet the team</div>
        <div className="grid grid-cols-3 gap-2">
          {["#c4b5a5", "#9aa8bc", "#b7c4a3"].map((tone, i) => (
            <div key={i} className="text-center">
              <div className={`mx-auto rounded-full ${compact ? "w-6 h-6" : "w-10 h-10"}`} style={{ background: tone }} />
              <div className={`bg-[#ddd] mt-1.5 mx-auto rounded ${compact ? "h-1 w-8" : "h-1.5 w-12"}`} />
              {!compact ? <div className="h-1 w-8 bg-[#eee] mt-1 mx-auto rounded" /> : null}
            </div>
          ))}
        </div>
      </div>,
    );
  }

  if (key === "faq" || key === "accordion") {
    return wrap(
      <div className="h-full p-4 space-y-2.5 flex flex-col justify-center">
        {["What is included?", "Who should attend?", "How do I register?"].map((q, i) => (
          <div key={i} className="border-b border-[#eee] pb-2">
            <div className={`font-medium text-[#222] ${compact ? "text-[7px]" : "text-[11px]"}`}>{compact ? "Question" : q}</div>
            {i === 0 && !compact ? <p className="text-[9px] text-[#888] mt-1 line-clamp-2">A short answer preview for this FAQ item.</p> : null}
          </div>
        ))}
      </div>,
    );
  }

  if (key === "gallery") {
    const tiles = [
      "from-[#d6c3b0] to-[#8f7460]",
      "from-[#b8c5d6] to-[#6f849c]",
      "from-[#c9d5b8] to-[#7a8f62]",
      "from-[#e0c4c4] to-[#a06d6d]",
      "from-[#cfc9e0] to-[#7d739c]",
      "from-[#d8d0bf] to-[#8a816c]",
    ];
    return wrap(
      <div className="h-full p-3 grid grid-cols-3 gap-1.5 content-center">
        {tiles.slice(0, compact ? 3 : 6).map((g, i) => (
          <div key={i} className={`aspect-square rounded-sm bg-gradient-to-br ${g} relative overflow-hidden`}>
            <div className="absolute bottom-0 inset-x-0 h-1/3 bg-gradient-to-t from-black/25 to-transparent" />
          </div>
        ))}
      </div>,
    );
  }

  if (key === "carousel") {
    return wrap(
      <div className="h-full flex flex-col">
        <div className="flex-1 relative bg-gradient-to-br from-[#9aa8bc] via-[#7d8da3] to-[#556579]">
          <div className="absolute inset-y-0 left-2 flex items-center">
            <div className="w-5 h-5 rounded-full bg-white/90 text-[#333] grid place-items-center text-[10px]">‹</div>
          </div>
          <div className="absolute inset-y-0 right-2 flex items-center">
            <div className="w-5 h-5 rounded-full bg-white/90 text-[#333] grid place-items-center text-[10px]">›</div>
          </div>
          {!compact ? (
            <div className="absolute bottom-3 left-3 right-3">
              <div className="text-white text-[11px] font-medium drop-shadow">Event gallery</div>
              <div className="text-white/80 text-[9px]">Swipe through highlights</div>
            </div>
          ) : null}
        </div>
        <div className="flex justify-center gap-1.5 py-2 bg-white">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`rounded-full ${i === 0 ? "w-4 h-1.5 bg-[#111]" : "w-1.5 h-1.5 bg-[#ccc]"}`} />
          ))}
        </div>
      </div>,
    );
  }

  if (key === "video") {
    return wrap(
      <div className="h-full flex flex-col bg-[#111]">
        <div className="flex-1 relative bg-gradient-to-br from-[#2a2a32] via-[#1a1a1f] to-[#0d0d10] flex items-center justify-center">
          <div className={`${compact ? "w-7 h-7" : "w-12 h-12"} rounded-full bg-white/95 grid place-items-center shadow-lg`}>
            <div
              aria-hidden
              style={{
                width: 0,
                height: 0,
                borderTop: `${compact ? 5 : 8}px solid transparent`,
                borderBottom: `${compact ? 5 : 8}px solid transparent`,
                borderLeft: `${compact ? 8 : 12}px solid #111`,
                marginLeft: 2,
              }}
            />
          </div>
          {!compact ? <div className="absolute bottom-2 left-2 text-[9px] text-white/70 font-mono">YouTube</div> : null}
        </div>
        {!compact ? (
          <div className="bg-white px-3 py-2">
            <div className="text-[11px] font-medium text-[#111]">Watch the highlight reel</div>
            <div className="text-[9px] text-[#888] mt-0.5">Embed a YouTube video</div>
          </div>
        ) : null}
      </div>,
    );
  }

  if (key === "quote") {
    return wrap(
      <div className="h-full flex flex-col items-center justify-center px-5 text-center bg-[#f7f5f1]">
        <div className={`text-[#c4b5a5] font-serif leading-none ${compact ? "text-xl" : "text-5xl"}`}>&ldquo;</div>
        <p className={`font-serif italic text-[#222] leading-snug max-w-[240px] ${compact ? "text-[8px]" : "text-sm md:text-base"}`}>
          {compact ? "Great event." : "This was the best event we attended all year."}
        </p>
        {!compact ? <p className="text-[10px] text-[#777] mt-3">— Alex Chen, Attendee</p> : null}
      </div>,
    );
  }

  if (key === "tickets" || key === "pricing") {
    return wrap(
      <div className="h-full bg-[#111] p-3 grid grid-cols-3 gap-2 items-stretch">
        {["Guest", "Pro", "VIP"].map((name, i) => (
          <div key={i} className={`border ${i === 1 ? "border-[#FF5608]" : "border-[#333]"} bg-[#1a1a1a] p-2 flex flex-col`}>
            <div className={`text-white/80 ${compact ? "text-[7px]" : "text-[10px]"}`}>{name}</div>
            {!compact ? <div className="text-white text-sm font-semibold mt-1">{i === 0 ? "Free" : i === 1 ? "$49" : "$99"}</div> : null}
            {!compact ? <div className="mt-auto h-5 bg-[#FF5608]/80 rounded-sm text-[8px] text-white grid place-items-center">Select</div> : null}
          </div>
        ))}
      </div>,
    );
  }

  if (key === "schedule" || key === "menu") {
    return wrap(
      <div className="h-full p-4 space-y-2.5 flex flex-col justify-center">
        {[
          ["09:30", "Opening keynote"],
          ["11:00", "Panel discussion"],
          ["14:00", "Workshops"],
        ].map(([time, label], i) => (
          <div key={i} className="flex items-center gap-3">
            <div className={`font-mono text-[#888] shrink-0 ${compact ? "text-[7px] w-8" : "text-[10px] w-12"}`}>{time}</div>
            <div className="flex-1 border-b border-[#eee] pb-1.5">
              <div className={`text-[#222] ${compact ? "text-[7px]" : "text-[11px] font-medium"}`}>{compact ? "Session" : label}</div>
            </div>
          </div>
        ))}
      </div>,
    );
  }

  if (key === "metrics") {
    return wrap(
      <div className="h-full p-4 grid grid-cols-3 gap-2 content-center">
        {[
          ["500+", "Attendees"],
          ["40", "Speakers"],
          ["12", "Tracks"],
        ].map(([value, label], i) => (
          <div key={i} className="text-center">
            <div className={`font-serif text-[#111] ${compact ? "text-[10px]" : "text-xl"}`}>{value}</div>
            {!compact ? <div className="text-[9px] text-[#888] mt-1">{label}</div> : null}
          </div>
        ))}
      </div>,
    );
  }

  if (key === "sponsors" || key === "logo-strip") {
    return wrap(
      <div className="h-full p-4 flex flex-col justify-center">
        {!compact ? <div className="text-center text-[10px] uppercase tracking-[0.2em] text-[#999] mb-3">Sponsors</div> : null}
        <div className="flex flex-wrap justify-center gap-3">
          {["ACME", "NORTH", "PULSE", "ORB"].slice(0, compact ? 2 : 4).map((name) => (
            <div key={name} className={`border border-[#e5e5e5] rounded px-3 py-2 text-[#666] font-semibold tracking-wider ${compact ? "text-[7px]" : "text-[11px]"}`}>
              {name}
            </div>
          ))}
        </div>
      </div>,
    );
  }

  if (key === "contact") {
    return wrap(
      <div className="h-full p-4 flex flex-col justify-center gap-2">
        <div className={`font-serif ${compact ? "text-[9px]" : "text-sm"}`}>Get in touch</div>
        {["Name", "Email", "Message"].map((label) => (
          <div key={label} className="border border-[#e5e5e5] rounded px-2 py-1.5 text-[9px] text-[#aaa]">{compact ? "" : label}</div>
        ))}
        {!compact ? <div className="mt-1 h-6 w-20 bg-[#111] rounded text-[9px] text-white grid place-items-center">Send</div> : null}
      </div>,
    );
  }

  if (key === "map") {
    return wrap(
      <div className="h-full relative bg-[#dce6d8]">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(#b7c4b0 1px, transparent 1px), linear-gradient(90deg, #b7c4b0 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className={`${compact ? "w-3 h-3" : "w-5 h-5"} rounded-full bg-[#FF5608] ring-4 ring-[#FF5608]/25`} />
        </div>
        {!compact ? <div className="absolute bottom-2 left-2 right-2 bg-white/95 rounded px-2 py-1.5 text-[10px] text-[#333]">The Meeting Point, One-North</div> : null}
      </div>,
    );
  }

  if (key === "newsletter") {
    return wrap(
      <div className="h-full bg-[#111] text-white flex flex-col items-center justify-center px-4 text-center">
        <div className={`font-serif ${compact ? "text-[9px]" : "text-sm"}`}>Stay in the loop</div>
        {!compact ? <p className="text-[9px] text-white/60 mt-1 mb-3">Get event updates by email</p> : null}
        <div className={`flex w-full max-w-[200px] ${compact ? "mt-1" : ""}`}>
          <div className="flex-1 bg-white/10 border border-white/20 rounded-l px-2 py-1.5 text-[8px] text-white/40">you@email.com</div>
          <div className="bg-[#FF5608] rounded-r px-2 py-1.5 text-[8px] font-bold">Join</div>
        </div>
      </div>,
    );
  }

  if (key === "socials") {
    return wrap(
      <div className="h-full flex items-center justify-center gap-3">
        {["in", "x", "yt", "ig"].slice(0, compact ? 3 : 4).map((s) => (
          <div key={s} className={`${compact ? "w-6 h-6 text-[8px]" : "w-9 h-9 text-[11px]"} rounded-full bg-[#111] text-white grid place-items-center uppercase font-bold`}>
            {s}
          </div>
        ))}
      </div>,
    );
  }

  if (key === "list-stacked" || type === "list") {
    return wrap(
      <div className="h-full p-4 space-y-2 flex flex-col justify-center">
        {["Curated agenda", "Expert speakers", "Networking"].map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className={`${compact ? "w-2 h-2 mt-0.5" : "w-3 h-3 mt-0.5"} rounded-full bg-[#FF5608]/80 shrink-0`} />
            <div>
              <div className={`text-[#222] ${compact ? "text-[7px]" : "text-[11px] font-medium"}`}>{item}</div>
              {!compact ? <div className="text-[9px] text-[#888]">Short supporting line</div> : null}
            </div>
          </div>
        ))}
      </div>,
    );
  }

  if (key === "charts") {
    return wrap(
      <div className="h-full p-4 flex items-end justify-center gap-2">
        {[40, 70, 55, 90, 65].map((pct, i) => (
          <div key={i} className="w-5 rounded-t bg-gradient-to-t from-[#FF5608] to-[#ffb089]" style={{ height: `${compact ? pct * 0.35 : pct * 0.7}%` }} />
        ))}
      </div>,
    );
  }

  if (key === "divider") {
    return wrap(
      <div className="h-full flex items-center px-6">
        <div className="flex-1 border-t border-[#ddd]" />
        <div className="mx-3 text-[10px] text-[#aaa]">·</div>
        <div className="flex-1 border-t border-[#ddd]" />
      </div>,
    );
  }

  if (key === "button") {
    return wrap(
      <div className="h-full flex items-center justify-center">
        <div className={`bg-[#111] text-white rounded px-5 py-2 font-medium ${compact ? "text-[8px]" : "text-xs"}`}>Register now</div>
      </div>,
    );
  }

  // Generic fallback — still readable content, not empty bars
  return wrap(
    <div className="h-full p-4 flex flex-col justify-center gap-2">
      <div className={`font-serif text-[#111] ${compact ? "text-[9px]" : "text-sm"}`}>{title}</div>
      <div className={`text-[#666] leading-relaxed ${compact ? "text-[7px] line-clamp-2" : "text-[10px] line-clamp-3"}`}>{sub}</div>
      {!compact ? <div className="mt-1 inline-block self-start bg-[#111] text-white text-[9px] px-3 py-1.5">Learn more</div> : null}
    </div>,
  );
}

function layoutForTemplate(type, idx) {
  const map = {
    list: ["stacked", "columns", "icon-row"],
    headline: ["centered", "left"],
    people: ["columns", "stacked"],
    pricing: ["columns", "compact"],
    gallery: ["grid-3", "grid-6"],
    charts: ["columns", "stacked"],
  };
  return map[type]?.[idx] || (idx % 2 === 0 ? "stacked" : "columns");
}

function previewLines(props, type) {
  if (Array.isArray(props?.items) && typeof props.items[0] === "string") {
    return props.items.slice(0, 3).map((line) => ({ title: line, body: "Short supporting description for this point." }));
  }
  if (Array.isArray(props?.items) && props.items[0]?.name) {
    return props.items.slice(0, 3).map((row) => ({ title: row.name, body: row.role || row.details || "" }));
  }
  if (Array.isArray(props?.plans)) {
    return props.plans.slice(0, 3).map((plan) => ({ title: plan.name, body: plan.price || "" }));
  }
  if (Array.isArray(props?.stats)) {
    return props.stats.slice(0, 3).map((stat) => ({ title: stat.label, body: stat.value || "" }));
  }
  return [
    { title: props?.title || "Weekly themes", body: props?.body || "You want to keep your audience engaged." },
    { title: "Supplies you own", body: "Use what you already have on hand." },
    { title: "Age-level learning", body: "Match each person at their level." },
  ];
}

function LayoutIcon({ index }) {
  const glyphs = ["◎", "✦", "123"];
  return (
    <div className="flex h-12 w-12 items-center justify-center text-xl text-[#1a73e8] font-light" aria-hidden>
      {glyphs[index % glyphs.length]}
    </div>
  );
}

function LayoutCta({ className = "" }) {
  return (
    <span className={`inline-block bg-[#111] text-white text-[13px] px-5 py-2.5 ${className}`}>
      Learn more
    </span>
  );
}

function LayoutVariantPreview({ type, layout, props = {} }) {
  const rows = previewLines(props, type);
  const sectionTitle = props?.title;

  if (type === "quote") {
    return (
      <div className="text-[#111] max-w-lg mx-auto text-center py-4">
        <p className="font-serif text-xl italic leading-relaxed">&ldquo;{props?.quote || "This was the best event we attended all year."}&rdquo;</p>
        <p className="text-sm text-[#666] mt-4">— {props?.author || "Attendee"}</p>
      </div>
    );
  }

  if (layout === "columns" || layout === "grid-3") {
    const cols = layout === "grid-3" ? 3 : Math.min(3, rows.length);
    return (
      <div className="relative text-[#111]">
        {type !== "gallery" ? <LayoutCta className="absolute top-0 right-0" /> : null}
        {sectionTitle ? <div className="font-serif text-2xl mb-8 text-center">{sectionTitle}</div> : null}
        <div className={`grid gap-8 pt-2 ${cols === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
          {rows.map((row, i) => (
            <div key={i} className="text-center">
              {type === "gallery" ? (
                <div className="aspect-[4/3] bg-[#ececef] mb-3" />
              ) : (
                <div className="flex justify-center mb-4"><LayoutIcon index={i} /></div>
              )}
              <div className="font-serif text-lg leading-snug">{row.title}</div>
              {row.body ? <p className="text-[13px] text-[#666] mt-2 leading-relaxed">{row.body}</p> : null}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (layout === "icon-row") {
    return (
      <div className="text-[#111]">
        {sectionTitle ? <div className="font-serif text-2xl text-center mb-8">{sectionTitle}</div> : null}
        <div className="flex justify-center gap-10 mb-8">
          {rows.map((_, i) => <LayoutIcon key={i} index={i} />)}
        </div>
        <div className="grid grid-cols-3 gap-6 text-center">
          {rows.map((row, i) => (
            <div key={i}>
              <div className="font-serif text-lg">{row.title}</div>
              <p className="text-[13px] text-[#666] mt-2">{row.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex justify-center"><LayoutCta /></div>
      </div>
    );
  }

  if (layout === "grid-6") {
    return (
      <div className="grid grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="aspect-square bg-[#ececef]" />
        ))}
      </div>
    );
  }

  if (layout === "centered") {
    return (
      <div className="text-center text-[#111] max-w-lg mx-auto py-4">
        <div className="font-serif text-3xl leading-tight">{props?.title || "Start the adventure today."}</div>
        <p className="text-[15px] text-[#666] mt-4 leading-relaxed">{props?.body || "A crisp statement under your nav."}</p>
        <div className="mt-6 flex justify-center"><LayoutCta /></div>
      </div>
    );
  }

  if (layout === "left") {
    return (
      <div className="text-[#111] max-w-md">
        <div className="font-serif text-3xl leading-tight">{props?.title || "Why attend"}</div>
        <p className="text-[15px] text-[#666] mt-4 leading-relaxed">{props?.body || "Tell visitors what they will get."}</p>
        <div className="mt-6"><LayoutCta /></div>
      </div>
    );
  }

  if (layout === "compact") {
    return (
      <div className="text-[#111] space-y-3 max-w-md">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between border-b border-[#eee] pb-3">
            <span className="font-medium text-sm">{row.title}</span>
            <span className="text-sm text-[#666]">{row.body}</span>
          </div>
        ))}
      </div>
    );
  }

  // Default: stacked list (Squarespace layout 1)
  return (
    <div className="flex gap-8 text-[#111]">
      <div className="flex-1 min-w-0 space-y-7">
        {sectionTitle ? <div className="font-serif text-2xl mb-2">{sectionTitle}</div> : null}
        {rows.map((row, i) => (
          <div key={i}>
            <div className="font-serif text-xl leading-snug">{row.title}</div>
            <p className="text-[14px] text-[#666] mt-2 leading-relaxed max-w-md">{row.body}</p>
          </div>
        ))}
        <LayoutCta className="mt-2" />
      </div>
      <div className="hidden sm:flex w-24 shrink-0 flex-col items-center gap-10 pt-6">
        {rows.map((_, i) => <LayoutIcon key={i} index={i} />)}
      </div>
    </div>
  );
}

function SectionBlockPreview({ type }) {
  if (type === "hero" || type === "headline" || type === "text") {
    return (
      <div className="mt-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-elev)] p-3 h-24">
        <div className="h-3 w-1/2 bg-[var(--primary)]/40 mb-2" />
        <div className="h-2 w-5/6 bg-[#2b2b32] mb-1.5" />
        <div className="h-2 w-4/6 bg-[#23232a] mb-4" />
        <div className="h-5 w-20 rounded bg-[var(--primary)]/30" />
      </div>
    );
  }
  if (type === "gallery" || type === "carousel" || type === "image") {
    return (
      <div className="mt-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-elev)] p-2 h-24 grid grid-cols-3 gap-1.5">
        <div className="bg-[#2a2a31]" />
        <div className="bg-[#23232a]" />
        <div className="bg-[#2a2a31]" />
        <div className="bg-[#23232a]" />
        <div className="bg-[#2a2a31]" />
        <div className="bg-[#23232a]" />
      </div>
    );
  }
  if (type === "pricing" || type === "tickets") {
    return (
      <div className="mt-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-elev)] p-2 h-24 grid grid-cols-3 gap-1.5">
        <div className="border border-[#2b2b32] p-1">
          <div className="h-1.5 w-full bg-[#2b2b32] mb-1" />
          <div className="h-2.5 w-2/3 bg-[var(--primary)]/35" />
        </div>
        <div className="border border-[#2b2b32] p-1">
          <div className="h-1.5 w-full bg-[#2b2b32] mb-1" />
          <div className="h-2.5 w-2/3 bg-[var(--primary)]/35" />
        </div>
        <div className="border border-[#2b2b32] p-1">
          <div className="h-1.5 w-full bg-[#2b2b32] mb-1" />
          <div className="h-2.5 w-2/3 bg-[var(--primary)]/35" />
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-lg border border-[var(--border-default)] bg-[var(--bg-elev)] p-3 h-24">
      <div className="h-full flex flex-col justify-between">
        <div className="h-2 w-2/3 bg-[var(--border-default)]" />
        <div className="space-y-1">
          <div className="h-2 w-full bg-[#1f1f23]" />
          <div className="h-2 w-5/6 bg-[#1f1f23]" />
        </div>
        <div className="h-2 w-1/2 bg-[var(--primary)]/50" />
      </div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-tertiary)] mt-2">{BLOCK_PREVIEW_TEXT[type] || "Section preview"}</div>
    </div>
  );
}

function PropEditor({ block, update, mediaLibrary, onUpload, onOpenLibrary }) {
  const p = block.props || {};
  const items = Array.isArray(p.items) ? p.items : [];
  const addItem = () => update("items", [...items, { q: "New question", a: "New answer" }]);
  const patchItem = (idx, next) => update("items", items.map((it, i) => (i === idx ? { ...it, ...next } : it)));

  switch (block.type) {
    case "hero":
      return (
        <>
          <Field label="Eyebrow"><input className="input-dark" value={p.eyebrow || ""} onChange={(ev) => update("eyebrow", ev.target.value)} /></Field>
          <Field label="Title"><input className="input-dark" value={p.title || ""} onChange={(ev) => update("title", ev.target.value)} /></Field>
          <Field label="Title accent"><input className="input-dark" value={p.titleAccent || ""} onChange={(ev) => update("titleAccent", ev.target.value)} /></Field>
          <Field label="Body"><textarea className="input-dark" rows={4} value={p.body || ""} onChange={(ev) => update("body", ev.target.value)} /></Field>
          <Field label="CTA label"><input className="input-dark" value={p.cta || ""} onChange={(ev) => update("cta", ev.target.value)} /></Field>
          <Field label="Hero image URL (optional override)"><input className="input-dark" value={p.imageUrl || ""} onChange={(ev) => update("imageUrl", ev.target.value)} /></Field>
          <button className="btn-secondary text-xs" onClick={onUpload}><Plus size={12} /> Upload image</button>
        </>
      );
    case "faq":
    case "accordion":
      return (
        <>
          <Field label="Title"><input className="input-dark" value={p.title || ""} onChange={(ev) => update("title", ev.target.value)} /></Field>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="surface p-2">
                <input className="input-dark mb-2" value={it.q || it.title || ""} onChange={(ev) => patchItem(i, { q: ev.target.value, title: ev.target.value })} placeholder="Question / Title" />
                <textarea className="input-dark" rows={2} value={it.a || it.body || ""} onChange={(ev) => patchItem(i, { a: ev.target.value, body: ev.target.value })} placeholder="Answer / Body" />
              </div>
            ))}
          </div>
          <button className="btn-secondary text-xs" onClick={addItem}><Plus size={12} /> Add row</button>
        </>
      );
    case "image":
      return (
        <>
          <Field label="Image source URL"><input className="input-dark" value={p.src || ""} onChange={(ev) => update("src", ev.target.value)} /></Field>
          <Field label="Caption"><input className="input-dark" value={p.caption || ""} onChange={(ev) => update("caption", ev.target.value)} /></Field>
          <button className="btn-secondary text-xs" onClick={onUpload}><Plus size={12} /> Upload image</button>
          <ImagePicker mediaLibrary={mediaLibrary} onPick={(u) => update("src", u)} onOpenLibrary={onOpenLibrary} />
        </>
      );
    case "gallery": {
      const imgs = Array.isArray(p.images) ? p.images : [];
      return (
        <>
          <button className="btn-secondary text-xs" onClick={onUpload}><Plus size={12} /> Upload image(s)</button>
          <ImagePicker mediaLibrary={mediaLibrary} onPick={(u) => update("images", [...imgs.slice(0, 5), u])} onOpenLibrary={onOpenLibrary} />
          <Field label="Gallery URLs (one per line)"><textarea className="input-dark" rows={5} value={imgs.join("\n")} onChange={(ev) => update("images", ev.target.value.split("\n").map((x) => x.trim()).filter(Boolean).slice(0, 6))} /></Field>
        </>
      );
    }
    case "logoStrip":
      return <Field label="Logos / names (one per line)"><textarea className="input-dark" rows={4} value={(Array.isArray(p.logos) ? p.logos : []).join("\n")} onChange={(ev) => update("logos", ev.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} /></Field>;
    case "text":
    case "headline":
      return (
        <>
          <Field label="Title"><input className="input-dark" value={p.title || ""} onChange={(ev) => update("title", ev.target.value)} /></Field>
          <Field label="Body"><textarea className="input-dark" rows={5} value={p.body || ""} onChange={(ev) => update("body", ev.target.value)} /></Field>
          {block.type === "headline" ? (
            <Field label="Alignment">
              <select className="input-dark" value={p.align || "left"} onChange={(ev) => update("align", ev.target.value)}>
                <option value="left">Left</option>
                <option value="center">Center</option>
              </select>
            </Field>
          ) : null}
        </>
      );
    case "list":
      return (
        <>
          <Field label="Title"><input className="input-dark" value={p.title || ""} onChange={(ev) => update("title", ev.target.value)} /></Field>
          <Field label="Items (one per line)"><textarea className="input-dark" rows={5} value={(Array.isArray(p.items) ? p.items : []).join("\n")} onChange={(ev) => update("items", ev.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} /></Field>
        </>
      );
    case "carousel":
      return (
        <>
          <button className="btn-secondary text-xs" onClick={onUpload}><Plus size={12} /> Upload images</button>
          <Field label="Image URLs (one per line)"><textarea className="input-dark" rows={5} value={(Array.isArray(p.images) ? p.images : []).join("\n")} onChange={(ev) => update("images", ev.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} /></Field>
        </>
      );
    case "video":
      return (
        <>
          <Field label="Title"><input className="input-dark" value={p.title || ""} onChange={(ev) => update("title", ev.target.value)} /></Field>
          <Field label="YouTube URL"><input className="input-dark" value={p.youtubeUrl || ""} onChange={(ev) => update("youtubeUrl", ev.target.value)} /></Field>
        </>
      );
    case "contact":
      return (
        <>
          <Field label="Title"><input className="input-dark" value={p.title || ""} onChange={(ev) => update("title", ev.target.value)} /></Field>
          <Field label="Email"><input className="input-dark" value={p.email || ""} onChange={(ev) => update("email", ev.target.value)} /></Field>
          <Field label="Phone"><input className="input-dark" value={p.phone || ""} onChange={(ev) => update("phone", ev.target.value)} /></Field>
          <Field label="CTA"><input className="input-dark" value={p.cta || ""} onChange={(ev) => update("cta", ev.target.value)} /></Field>
        </>
      );
    case "socials":
      return <Field label="Social labels (one per line)"><textarea className="input-dark" rows={4} value={(Array.isArray(p.links) ? p.links.map((x) => x.label).filter(Boolean) : []).join("\n")} onChange={(ev) => update("links", ev.target.value.split("\n").map((x) => x.trim()).filter(Boolean).map((label) => ({ label, url: "#" })))} /></Field>;
    case "charts":
      return <Field label="Stats (label:value per line)"><textarea className="input-dark" rows={5} value={(Array.isArray(p.stats) ? p.stats.map((x) => `${x.label}:${x.value}`) : []).join("\n")} onChange={(ev) => update("stats", ev.target.value.split("\n").map((row) => row.trim()).filter(Boolean).map((row) => { const [label, value] = row.split(":"); return { label: (label || "").trim(), value: (value || "").trim() }; }))} /></Field>;
    case "menu":
      return <Field label="Menu rows (name:details per line)"><textarea className="input-dark" rows={5} value={(Array.isArray(p.items) ? p.items.map((x) => `${x.name}:${x.details}`) : []).join("\n")} onChange={(ev) => update("items", ev.target.value.split("\n").map((row) => row.trim()).filter(Boolean).map((row) => { const [name, details] = row.split(":"); return { name: (name || "").trim(), details: (details || "").trim() }; }))} /></Field>;
    case "people":
      return (
        <>
          <Field label="Title"><input className="input-dark" value={p.title || ""} onChange={(ev) => update("title", ev.target.value)} /></Field>
          <Field label="People (name:role per line)"><textarea className="input-dark" rows={5} value={(Array.isArray(p.items) ? p.items.map((x) => `${x.name}:${x.role}`) : []).join("\n")} onChange={(ev) => update("items", ev.target.value.split("\n").map((row) => row.trim()).filter(Boolean).map((row) => { const [name, role] = row.split(":"); return { name: (name || "").trim(), role: (role || "").trim() }; }))} /></Field>
        </>
      );
    case "pricing":
      return (
        <>
          <Field label="Title"><input className="input-dark" value={p.title || ""} onChange={(ev) => update("title", ev.target.value)} /></Field>
          <Field label="Plans (name:price per line)"><textarea className="input-dark" rows={5} value={(Array.isArray(p.plans) ? p.plans.map((x) => `${x.name}:${x.price}`) : []).join("\n")} onChange={(ev) => update("plans", ev.target.value.split("\n").map((row) => row.trim()).filter(Boolean).map((row) => { const [name, price] = row.split(":"); return { name: (name || "").trim(), price: (price || "").trim() }; }))} /></Field>
        </>
      );
    case "button":
      return (
        <>
          <Field label="Label"><input className="input-dark" value={p.label || ""} onChange={(ev) => update("label", ev.target.value)} /></Field>
          <Field label="URL"><input className="input-dark" value={p.href || ""} onChange={(ev) => update("href", ev.target.value)} /></Field>
        </>
      );
    case "map":
      return (
        <>
          <Field label="Address"><input className="input-dark" value={p.address || ""} onChange={(ev) => update("address", ev.target.value)} /></Field>
          <Field label="Embed URL (optional)"><input className="input-dark" value={p.embedUrl || ""} onChange={(ev) => update("embedUrl", ev.target.value)} /></Field>
        </>
      );
    case "quote":
      return (
        <>
          <Field label="Quote"><textarea className="input-dark" rows={4} value={p.quote || ""} onChange={(ev) => update("quote", ev.target.value)} /></Field>
          <Field label="Author"><input className="input-dark" value={p.author || ""} onChange={(ev) => update("author", ev.target.value)} /></Field>
        </>
      );
    case "sponsors":
      return <Field label="Caption"><input className="input-dark" value={p.caption || ""} onChange={(ev) => update("caption", ev.target.value)} /></Field>;
    case "schedule":
    case "speakers":
      return (
        <>
          <Field label="Eyebrow"><input className="input-dark" value={p.eyebrow || ""} onChange={(ev) => update("eyebrow", ev.target.value)} /></Field>
          <Field label="Title"><input className="input-dark" value={p.title || ""} onChange={(ev) => update("title", ev.target.value)} /></Field>
        </>
      );
    case "cta":
      return (
        <>
          <Field label="Headline"><input className="input-dark" value={p.title || ""} onChange={(ev) => update("title", ev.target.value)} /></Field>
          <Field label="Button label"><input className="input-dark" value={p.cta || ""} onChange={(ev) => update("cta", ev.target.value)} /></Field>
        </>
      );
    default:
      return <div className="text-xs text-[var(--text-tertiary)]">No editable fields for this block yet.</div>;
  }
}

function ImagePicker({ mediaLibrary, onPick, onOpenLibrary }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-overline">Pick from library</div>
        {onOpenLibrary ? (
          <button type="button" className="text-[10px] font-mono uppercase tracking-wider text-[var(--primary)]" onClick={onOpenLibrary}>
            Browse all
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-4 gap-2 max-h-28 overflow-y-auto">
        {(mediaLibrary || []).slice(0, 20).map((u, i) => (
          <button key={`${String(u).slice(0, 24)}-${i}`} type="button" className="aspect-square border border-[var(--border-default)] overflow-hidden" onClick={() => onPick(u)}>
            <img src={resolveMediaUrl(u)} alt="asset" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-overline">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function BlockWrap({ active, onClick, children, style }) {
  return (
    <div
      onClick={onClick}
      style={style}
      className={`relative cursor-pointer transition ${active ? "outline outline-2 outline-[var(--primary)] outline-offset-[-2px]" : "hover:outline hover:outline-1 hover:outline-[#27272A] hover:outline-offset-[-1px]"}`}
    >
      {children}
    </div>
  );
}

function BlockRender({
  block,
  theme,
  active,
  onClick,
  event,
  speakersPreview = [],
  sessionsData = [],
  sponsorsData = [],
  exhibitorsData = [],
  ticketRows = [],
  mediaLibrary = [],
  onInlineEdit,
}) {
  const p = block.props || {};
  const accent = p.accentColor || theme.accent;
  const surface = theme.surface;
  const sectionStyle = {
    color: p.textColor || undefined,
    fontFamily: p.fontFamily || undefined,
  };

  switch (block.type) {
    case "hero": {
      const heroImg = resolveMediaUrl(p.imageUrl || event.cover);
      return (
        <BlockWrap active={active} onClick={onClick} style={sectionStyle}>
          <div className="relative px-10 pt-16 pb-20 overflow-hidden" style={{ background: theme.bg }}>
            <div className="absolute inset-0 opacity-30">
              {heroImg ? (
                <img src={heroImg} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${accent}44, ${theme.bg})` }} />
              )}
              <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${theme.bg}, transparent)` }} />
            </div>
            <div className="relative">
              <EditableText className="text-overline mb-4" value={p.eyebrow || ""} onSave={(v) => onInlineEdit("eyebrow", v)} />
              <div className="font-heading text-6xl leading-[0.95] tracking-tight font-semibold"><EditableText value={p.title || ""} onSave={(v) => onInlineEdit("title", v)} /><em className="not-italic block" style={{ color: accent }}><EditableText value={p.titleAccent || ""} onSave={(v) => onInlineEdit("titleAccent", v)} /></em></div>
              <EditableText className="mt-6 text-base text-[var(--text-secondary)] max-w-md" value={p.body || ""} onSave={(v) => onInlineEdit("body", v)} />
              <button className="mt-8 px-5 py-2.5 text-sm font-medium" style={{ background: accent, color: theme.bg }}><EditableText value={p.cta || "Save your seat"} onSave={(v) => onInlineEdit("cta", v)} /></button>
            </div>
          </div>
        </BlockWrap>
      );
    }
    case "metrics":
      return <BlockWrap active={active} onClick={onClick} style={sectionStyle}><div className="grid grid-cols-2 md:grid-cols-4 border-y" style={{ borderColor: "var(--border-default)", background: surface }}>{[["DATES", `${event.startDate} → ${event.endDate}`], ["VENUE", event.venue], ["TIMEZONE", "Event locale"], ["FORMAT", "In-person"]].map(([k, v]) => <div key={k} className="p-5 border-r last:border-r-0" style={{ borderColor: "var(--border-default)" }}><div className="text-overline">{k}</div><div className="font-mono text-xs mt-1.5">{v}</div></div>)}</div></BlockWrap>;
    case "schedule":
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-16 border-y" style={{ background: surface, borderColor: "var(--border-default)" }}><EditableText className="text-overline mb-3" value={p.eyebrow || "Schedule"} onSave={(v) => onInlineEdit("eyebrow", v)} /><EditableText className="font-heading text-3xl tracking-tight font-medium mb-6" value={p.title || "Program"} onSave={(v) => onInlineEdit("title", v)} /><div className="divide-y border-y" style={{ borderColor: "var(--border-default)" }}>{(sessionsData || []).slice(0, 4).map((s) => <div key={s.id} className="grid grid-cols-12 gap-3 py-4"><div className="col-span-2 font-mono text-xs text-[var(--text-secondary)]">{String(s.time || "").split(" · ")[1] || "TBD"}</div><div className="col-span-7"><div className="font-heading text-base">{s.title}</div><div className="text-[10px] font-mono text-[var(--text-tertiary)]">{s.room}</div></div><div className="col-span-3 text-right"><span className="text-[10px] font-mono uppercase border px-2 py-1" style={{ borderColor: "var(--border-default)", color: "#71717A" }}>{Number(s.registered || 0) >= Number(s.capacity || 0) ? "WAITLIST" : `${Math.max(0, Number(s.capacity || 0) - Number(s.registered || 0))} LEFT`}</span></div></div>)}</div></div></BlockWrap>;
    case "speakers": {
      return (
        <BlockWrap active={active} onClick={onClick}>
          <div className="px-10 py-16" style={{ background: theme.bg }}>
            <EditableText className="text-overline mb-3" value={p.eyebrow || "Speakers"} onSave={(v) => onInlineEdit("eyebrow", v)} />
            <EditableText className="font-heading text-3xl tracking-tight font-medium mb-6" value={p.title || "Speaker lineup"} onSave={(v) => onInlineEdit("title", v)} />
            <SpeakersGrid
              speakers={speakersPreview}
              accent={accent}
              emptyMessage="Add speakers under Attendees → Speakers to populate this block."
            />
          </div>
        </BlockWrap>
      );
    }
    case "tickets":
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-16 border-y" style={{ background: theme.bg, borderColor: "var(--border-default)" }}><div className="grid grid-cols-3 gap-px" style={{ background: "var(--border-default)" }}>{(ticketRows.length ? ticketRows : [{ id: "fallback-tier", name: "General", price: 0, sold: 0, quota: 0, status: "Unavailable" }]).slice(0, 3).map((t) => { const quota = Number(t?.quota || 0); const sold = Number(t?.sold || 0); const left = Math.max(0, quota - sold); const soldOut = quota > 0 && sold >= quota; return <div key={t?.id || t?.name || `${t?.price || 0}`} className="p-5" style={{ background: surface }}><div className="font-heading text-lg">{t?.name || "Untitled tier"}</div><div className="font-mono text-3xl mt-3">${Number(t?.price || 0).toLocaleString()}</div><div className="text-[10px] uppercase tracking-widest font-mono text-[var(--text-tertiary)] mt-2">{soldOut ? "Sold out" : left > 0 ? `${left} left` : t?.status || "Selling"}</div></div>; })}</div></div></BlockWrap>;
    case "sponsors": {
      const logos = (Array.isArray(p.logos) && p.logos.length ? p.logos : sponsorsData)
        .map((s) => (typeof s === "string" ? s : s?.name || s?.label || "Sponsor"))
        .filter(Boolean);
      return <BlockWrap active={active} onClick={onClick}><div className="border-y py-6 overflow-hidden" style={{ borderColor: "var(--border-default)", background: theme.bg }}><EditableText className="text-overline text-center mb-3" value={p.caption || "Sponsors"} onSave={(v) => onInlineEdit("caption", v)} /><div className="flex gap-12 ticker whitespace-nowrap">{[...logos, ...logos].map((s, i) => <span key={i} className="font-heading text-lg tracking-[0.3em] text-[var(--border-hover)]">{s}</span>)}</div></div></BlockWrap>;
    }
    case "exhibitors": {
      const rows = (Array.isArray(p.items) && p.items.length ? p.items : exhibitorsData)
        .map((row) => (typeof row === "string" ? { name: row, booth: "" } : row))
        .filter((row) => row?.name);
      return (
        <BlockWrap active={active} onClick={onClick}>
          <div className="px-6 md:px-10 py-14" style={{ background: theme.bg }}>
            <EditableText className="font-heading text-3xl" value={p.title || "Exhibitors"} onSave={(v) => onInlineEdit("title", v)} />
            <EditableText className="text-sm text-[var(--text-secondary)] mt-2" value={p.caption || "On the show floor"} onSave={(v) => onInlineEdit("caption", v)} />
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              {rows.slice(0, 12).map((row, idx) => (
                <div key={`${row.name}-${idx}`} className="border border-[var(--border-default)] p-4 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium">{row.name}</div>
                    {row.tier ? <div className="text-xs text-[var(--text-tertiary)] mt-1">{row.tier}</div> : null}
                  </div>
                  {row.booth ? <span className="font-mono text-xs text-[var(--primary)]">{row.booth}</span> : null}
                </div>
              ))}
            </div>
          </div>
        </BlockWrap>
      );
    }
    case "text":
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-16" style={{ background: theme.bg }}><EditableText className="font-heading text-4xl tracking-tight" value={p.title || "Section title"} onSave={(v) => onInlineEdit("title", v)} /><EditableText className="mt-4 text-[var(--text-secondary)] max-w-2xl" value={p.body || "Body copy"} onSave={(v) => onInlineEdit("body", v)} /></div></BlockWrap>;
    case "headline":
      return <BlockWrap active={active} onClick={onClick} style={sectionStyle}><div className={`px-6 md:px-10 py-16 ${p.align === "center" ? "text-center" : "text-left"}`} style={{ background: theme.bg }}><EditableText className="font-heading text-4xl md:text-6xl tracking-tight" value={p.title || "Headline"} onSave={(v) => onInlineEdit("title", v)} /><EditableText className="mt-4 text-[var(--text-secondary)] max-w-3xl mx-auto" value={p.body || "Supporting text"} onSave={(v) => onInlineEdit("body", v)} /></div></BlockWrap>;
    case "list": {
      const items = Array.isArray(p.items) && p.items.length ? p.items : ["Point one", "Point two"];
      return <BlockWrap active={active} onClick={onClick} style={sectionStyle}><div className="px-6 md:px-10 py-14" style={{ background: theme.bg }}><EditableText className="font-heading text-3xl" value={p.title || "List title"} onSave={(v) => onInlineEdit("title", v)} /><ul className="mt-5 space-y-3">{items.map((it, idx) => <li key={idx} className="flex items-start gap-3"><span style={{ color: accent }}>•</span><span>{it}</span></li>)}</ul></div></BlockWrap>;
    }
    case "carousel": {
      const imgs = Array.isArray(p.images) && p.images.length ? p.images : [event.cover, ...mediaLibrary.slice(0, 2)];
      return <BlockWrap active={active} onClick={onClick} style={sectionStyle}><div className="px-4 md:px-10 py-10" style={{ background: theme.bg }}><div className="grid grid-cols-1 md:grid-cols-3 gap-3">{imgs.slice(0, 5).map((u, i) => <div key={i} className="aspect-[16/10] overflow-hidden border border-[var(--border-default)]"><img src={u} alt="carousel" className="w-full h-full object-cover" /></div>)}</div></div></BlockWrap>;
    }
    case "video":
      return <BlockWrap active={active} onClick={onClick} style={sectionStyle}><div className="px-4 md:px-10 py-14" style={{ background: theme.bg }}><EditableText className="font-heading text-3xl" value={p.title || "Video"} onSave={(v) => onInlineEdit("title", v)} /><div className="mt-5 aspect-video border border-[var(--border-default)]">{String(p.youtubeUrl || "").includes("youtube") ? <iframe title="YouTube" src={String(p.youtubeUrl).replace("watch?v=", "embed/")} className="w-full h-full" /> : <div className="h-full grid place-items-center text-sm text-[var(--text-tertiary)]">Paste a YouTube URL in properties</div>}</div></div></BlockWrap>;
    case "contact":
      return <BlockWrap active={active} onClick={onClick} style={sectionStyle}><div className="px-6 md:px-10 py-14 grid md:grid-cols-2 gap-6" style={{ background: surface }}><div><EditableText className="font-heading text-3xl" value={p.title || "Contact us"} onSave={(v) => onInlineEdit("title", v)} /><div className="mt-4 text-sm text-[var(--text-secondary)]">{p.email || "hello@example.com"} · {p.phone || ""}</div></div><div className="surface p-4 border border-[var(--border-default)]"><input className="input-dark w-full" placeholder="Your email" /><button className="mt-3 px-4 py-2 text-sm font-medium" style={{ background: accent, color: theme.bg }}>{p.cta || "Send"}</button></div></div></BlockWrap>;
    case "socials": {
      const links = Array.isArray(p.links) && p.links.length ? p.links : [{ label: "Instagram", url: "#" }, { label: "LinkedIn", url: "#" }];
      return <BlockWrap active={active} onClick={onClick} style={sectionStyle}><div className="px-6 md:px-10 py-12" style={{ background: theme.bg }}><EditableText className="font-heading text-3xl" value={p.title || "Socials"} onSave={(v) => onInlineEdit("title", v)} /><div className="mt-4 flex flex-wrap gap-2">{links.map((link, idx) => <span key={idx} className="px-3 py-1 border border-[var(--border-default)] text-xs">{link.label}</span>)}</div></div></BlockWrap>;
    }
    case "charts": {
      const stats = Array.isArray(p.stats) && p.stats.length ? p.stats : [{ label: "Attendees", value: "2,400" }, { label: "NPS", value: "62" }];
      return <BlockWrap active={active} onClick={onClick} style={sectionStyle}><div className="px-6 md:px-10 py-12" style={{ background: theme.bg }}><EditableText className="font-heading text-3xl" value={p.title || "Highlights"} onSave={(v) => onInlineEdit("title", v)} /><div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">{stats.map((s, i) => <div key={i} className="border border-[var(--border-default)] p-4"><div className="font-mono text-2xl" style={{ color: accent }}>{s.value}</div><div className="text-xs text-[var(--text-tertiary)] mt-1">{s.label}</div></div>)}</div></div></BlockWrap>;
    }
    case "menu": {
      const items = Array.isArray(p.items) && p.items.length ? p.items : [{ name: "Breakfast", details: "08:00 - 09:00" }];
      return <BlockWrap active={active} onClick={onClick} style={sectionStyle}><div className="px-6 md:px-10 py-14" style={{ background: surface }}><EditableText className="font-heading text-3xl" value={p.title || "Menu"} onSave={(v) => onInlineEdit("title", v)} /><div className="mt-5 divide-y border-y border-[var(--border-default)]">{items.map((it, idx) => <div key={idx} className="py-3 flex justify-between text-sm"><span>{it.name}</span><span className="text-[var(--text-secondary)]">{it.details}</span></div>)}</div></div></BlockWrap>;
    }
    case "people": {
      const ppl = Array.isArray(p.items) && p.items.length ? p.items : [{ name: "Alex", role: "Host" }];
      return <BlockWrap active={active} onClick={onClick} style={sectionStyle}><div className="px-6 md:px-10 py-14" style={{ background: theme.bg }}><EditableText className="font-heading text-3xl" value={p.title || "People"} onSave={(v) => onInlineEdit("title", v)} /><div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">{ppl.map((person, idx) => <div key={idx} className="border border-[var(--border-default)] p-4"><div className="font-heading">{person.name}</div><div className="text-xs text-[var(--text-tertiary)] mt-1">{person.role}</div></div>)}</div></div></BlockWrap>;
    }
    case "pricing": {
      const plans = Array.isArray(p.plans) && p.plans.length ? p.plans : [{ name: "Standard", price: "$199" }];
      return <BlockWrap active={active} onClick={onClick} style={sectionStyle}><div className="px-6 md:px-10 py-14" style={{ background: theme.bg }}><EditableText className="font-heading text-3xl" value={p.title || "Pricing"} onSave={(v) => onInlineEdit("title", v)} /><div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">{plans.map((plan, idx) => <div key={idx} className="border border-[var(--border-default)] p-5"><div className="font-heading text-lg">{plan.name}</div><div className="font-mono text-3xl mt-2" style={{ color: accent }}>{plan.price}</div></div>)}</div></div></BlockWrap>;
    }
    case "image":
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-10" style={{ background: theme.bg }}><div className="aspect-[16/7] overflow-hidden border border-[var(--border-default)]"><img src={p.src || mediaLibrary[0] || event.cover} alt="block" className="w-full h-full object-cover" /></div>{p.caption ? <div className="text-xs text-[var(--text-tertiary)] mt-3">{p.caption}</div> : null}</div></BlockWrap>;
    case "gallery": {
      const imgs = Array.isArray(p.images) && p.images.length ? p.images : [event.cover, ...mediaLibrary.slice(0, 2)];
      return <BlockWrap active={active} onClick={onClick}><div className="grid grid-cols-3 gap-px" style={{ background: "var(--border-default)" }}>{imgs.slice(0, 6).map((u, i) => <div key={i} className="aspect-[4/3] overflow-hidden"><img src={u} alt="gallery" className="w-full h-full object-cover opacity-80" /></div>)}</div></BlockWrap>;
    }
    case "accordion":
    case "faq": {
      const rows = Array.isArray(p.items) && p.items.length ? p.items : [{ q: "Question", a: "Answer" }];
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-16" style={{ background: theme.bg }}><div className="font-heading text-3xl mb-6">{p.title || (block.type === "faq" ? "FAQ" : "Accordion")}</div><div className="divide-y border-y" style={{ borderColor: "var(--border-default)" }}>{rows.map((it, i) => <div key={i} className="py-4"><div className="flex items-center justify-between"><span>{it.q || it.title}</span><ChevronDown size={14} className="text-[var(--text-tertiary)]" /></div><p className="text-sm text-[var(--text-secondary)] mt-2">{it.a || it.body}</p></div>)}</div></div></BlockWrap>;
    }
    case "logoStrip": {
      const logos = (Array.isArray(p.logos) && p.logos.length ? p.logos : sponsorsData)
        .map((s) => (typeof s === "string" ? s : s?.name || s?.label || "Sponsor"))
        .filter(Boolean);
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-12 border-y" style={{ background: theme.bg, borderColor: "var(--border-default)" }}><div className="text-overline mb-4">{p.title || "Trusted by"}</div><div className="grid grid-cols-4 gap-px" style={{ background: "var(--border-default)" }}>{logos.slice(0, 8).map((l, i) => <div key={i} className="p-4 text-center font-mono text-xs" style={{ background: surface }}>{l}</div>)}</div></div></BlockWrap>;
    }
    case "quote":
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-16 border-y" style={{ background: surface, borderColor: "var(--border-default)" }}><Quote size={32} style={{ color: accent }} strokeWidth={1.4} /><p className="font-heading text-2xl tracking-tight mt-4 max-w-2xl leading-snug">"<EditableText value={p.quote || "Great event."} onSave={(v) => onInlineEdit("quote", v)} />"</p><div className="text-xs text-[var(--text-secondary)] mt-4 font-mono">— <EditableText value={p.author || "Name"} onSave={(v) => onInlineEdit("author", v)} /></div></div></BlockWrap>;
    case "divider":
      return <BlockWrap active={active} onClick={onClick}><div style={{ background: theme.bg, paddingTop: `${Number(p.spacing || 40)}px`, paddingBottom: `${Number(p.spacing || 40)}px` }}><div className="mx-10 border-t" style={{ borderColor: "var(--border-default)" }} /></div></BlockWrap>;
    case "button":
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-12" style={{ background: theme.bg }}><button className="px-5 py-2.5 text-sm font-medium" style={{ background: accent, color: theme.bg }}>{p.label || "Learn more"}</button></div></BlockWrap>;
    case "map":
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-16 grid grid-cols-2 gap-8 items-center" style={{ background: theme.bg }}><div><div className="text-overline mb-3">Venue</div><div className="font-heading text-2xl tracking-tight font-medium">{event.venue}</div><div className="text-sm text-[var(--text-secondary)] mt-3">{p.address || "89 South Street · New York, NY"}</div></div><div className="aspect-[4/3] dot-grid relative" style={{ background: surface }}>{p.embedUrl ? <iframe title="map" src={p.embedUrl} className="absolute inset-0 w-full h-full" /> : <MapPin size={32} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ color: accent }} />}</div></div></BlockWrap>;
    case "newsletter":
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-16 border-y" style={{ background: surface, borderColor: "var(--border-default)" }}><div className="text-overline mb-3">Newsletter</div><div className="font-heading text-3xl tracking-tight font-medium">Stay in the loop.</div><div className="mt-6 flex gap-2 max-w-md"><input className="input-dark" placeholder="you@operator.io" /><button className="px-4 py-2 text-sm font-medium" style={{ background: accent, color: theme.bg }}>Subscribe</button></div></div></BlockWrap>;
    case "cta":
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-16 border-t" style={{ background: theme.bg, borderColor: "var(--border-default)" }}><EditableText className="font-heading text-5xl leading-[0.95] tracking-tight font-semibold" value={p.title || "Be in the room."} onSave={(v) => onInlineEdit("title", v)} /><button className="mt-8 px-5 py-2.5 text-sm font-medium" style={{ background: accent, color: theme.bg }}><EditableText value={p.cta || "Register"} onSave={(v) => onInlineEdit("cta", v)} /></button></div></BlockWrap>;
    default:
      return <BlockWrap active={active} onClick={onClick}><div className="px-10 py-12 border-y text-center text-sm font-mono uppercase tracking-widest text-[var(--text-tertiary)]" style={{ background: surface, borderColor: "var(--border-default)" }}>{block.type} block</div></BlockWrap>;
  }
}

function EditableText({ value, onSave, className = "" }) {
  return (
    <span className={`${className} inline-block min-w-[12px] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]/60`} contentEditable suppressContentEditableWarning onClick={(ev) => ev.stopPropagation()} onBlur={(ev) => onSave(ev.currentTarget.textContent || "")} onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); ev.currentTarget.blur(); } }}>
      {value}
    </span>
  );
}
